import { useParams, Link } from 'react-router-dom';
import { findServiceBySlug } from '@/data/services';
import { serviceHeroImages } from '@/data/images';
import SEO from '@/components/shared/SEO';
import { usePopup } from '@/contexts/PopupContext';
import PricingSection from '@/components/shared/PricingSection';
import TestimonialSlider from '@/components/shared/TestimonialSlider';
import CTABanner from '@/components/shared/CTABanner';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from '@/components/ui/carousel';
import { ArrowRight, CheckCircle, ChevronRight } from 'lucide-react';

export default function ServicePage() {
  const { slug } = useParams();
  const { openPopup } = usePopup();
  const service = findServiceBySlug(slug);

  if (!service) {
    return (
      <div className="pt-32 pb-20 text-center" data-testid="service-not-found">
        <h1 className="font-heading font-bold text-3xl text-[#064E3B] mb-4">Service Not Found</h1>
        <p className="text-slate-600 mb-8">The service you are looking for does not exist.</p>
        <Link to="/" className="text-[#10B981] font-medium hover:underline">Return to Home</Link>
      </div>
    );
  }

  const Icon = service.categoryIcon;
  const heroImage = serviceHeroImages[service.categoryId] || serviceHeroImages.trademark;

  return (
    <div data-testid="service-page">
      <SEO
        title={`${service.title} - Professional ${service.category}`}
        description={service.heroSubtitle}
      />
      {/* 1. Service Hero */}
      <section className="relative pt-28 pb-12 lg:pt-36 lg:pb-24 overflow-hidden" data-testid="service-hero">
        <div className="absolute inset-0 z-0">
          <img src={heroImage} alt="" className="w-full h-full object-cover" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#064E3B]/95 via-[#064E3B]/80 to-[#064E3B]/50" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Breadcrumbs */}
          <nav className="flex items-center gap-1.5 text-sm text-emerald-200 mb-6" data-testid="service-breadcrumbs">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className="text-emerald-300">{service.category}</span>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className="text-white font-medium">{service.title}</span>
          </nav>
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm text-white px-4 py-1.5 rounded-full text-xs font-semibold mb-5 tracking-wide border border-white/20">
              <Icon className="w-3.5 h-3.5" /> {service.category.toUpperCase()}
            </div>
            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl text-white leading-tight tracking-tight mb-6">
              {service.title}
            </h1>
            <p className="text-emerald-100 text-base lg:text-lg leading-relaxed mb-8">
              {service.heroSubtitle}
            </p>
            <button
              onClick={() => openPopup()}
              className="bg-white text-[#064E3B] px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-emerald-50 transition-colors flex items-center gap-2 btn-pulse"
              data-testid="service-hero-cta"
            >
              {slug === 'active-monitoring-trademark-infringement' ? 'Start Monitoring My Trademark' : slug === 'clearance-search-services' ? 'Get Your Trademark Search' : slug === 'file-ttab-petition' ? 'File My TTAB Petition Now' : slug === 'trademark-ownership-transfers' ? 'Transfer Your Trademark Now' : slug === 'submission-of-statements-of-use' ? 'Submit My Statement of Use' : slug === 'support-response-trademark-office-actions' ? 'Respond to My Office Action Now' : slug === 'register-your-copyright' ? 'Register Your Copyright Today' : slug === 'copyright-enforcement-assistance' ? 'Protect My Copyright Now' : slug === 'copyright-transfers-licensing' ? 'Manage My Copyright License' : slug === 'legal-representation-copyright' ? 'Protect My Copyright' : slug === 'copyright-portfolio-management' ? 'Manage My Copyright Portfolio' : slug === 'dmca-compliance' ? 'Ensure DMCA Compliance' : slug === 'business-immigration-law' ? 'Get Business Immigration Help' : slug === 'due-diligence-process' ? 'Start Your Due Diligence Review' : slug === 'it-commercial-law' ? 'Get IT Commercial Law Assistance Now' : slug === 'partnerships-llps' ? 'Establish My Partnership or LLP Now' : slug === 'data-protection-privacy-law' ? 'Ensure Data Privacy Compliance Now' : slug === 'limited-liability-company' ? 'Form My LLC Now' : slug === 'c-corporation' ? 'Incorporate My C Corporation' : slug === 's-corporation' ? 'Form My S Corporation Now' : slug === 'non-profit' ? 'Establish My Non-Profit Now' : slug === 'search-existing-patent' ? 'Start My Patent Search Now' : slug === 'patent-office-actions-support' ? 'Get Patent Office Actions Support Now' : slug === 'patent-application-submission' ? 'Submit My Patent Application' : slug === 'legal-representation-patent' ? 'Protect My Patent Now' : slug === 'amazon-brand-registry' ? 'Register My Brand on Amazon Now' : slug === 'bookkeeping-service' ? 'Get Professional Bookkeeping Now' : slug === 'business-formation-services' ? 'Start My Business Formation Now' : slug === 'certificate-good-standing' ? 'Get My Certificate of Good Standing Now' : slug === 'company-dissolution' ? 'Dissolve My Company Now' : slug === 'company-name-change' ? 'Change My Company Name Now' : 'Get Started'} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* 2. Detailed Content + Image */}
      {slug === 'active-monitoring-trademark-infringement' ? (
        <section className="py-12 lg:py-28 bg-white" data-testid="service-content">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl text-[#064E3B] mb-6">
                  What Our Trademark Monitoring Service Covers
                </h2>
                <p className="text-slate-600 leading-relaxed mb-6">
                  Trademark Pact's active monitoring service ensures your registered trademark stays protected by continuously scanning for unauthorized use, conflicting applications, and potential infringement across all relevant channels. It provides a thorough watch across trademark databases, online platforms, and marketplace listings. This helps detect emerging threats early and accurately identifies every risk to your trademark rights before it escalates.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Infringement Detection:</strong> Identify and flag unauthorized use of your trademark across online platforms, marketplaces, and trademark databases before violations cause serious brand damage.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Conflict Monitoring:</strong> Track newly filed trademark applications that may conflict with your existing mark and receive timely alerts to take immediate protective action.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Expert Assessment:</strong> Receive professional analysis of every monitoring alert with clear, actionable recommendations on the most effective steps to defend your trademark.</span>
                  </div>
                </div>
              </div>
              <div>
                <img
                  src={heroImage}
                  alt={service.title}
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 'clearance-search-services' ? (
        <section className="py-12 lg:py-28 bg-white" data-testid="service-content">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl text-[#064E3B] mb-6">
                  What Our Search Service Covers
                </h2>
                <p className="text-slate-600 leading-relaxed mb-6">
                  Our trademark clearance search service ensures your brand is legally available for use and registration before investing. It provides a comprehensive analysis of existing trademarks across federal, state, and common law levels. This helps identify potential conflicts and accurately assesses the registrability of your proposed mark.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Risk Mitigation:</strong> Identify conflicting trademarks before investing in branding and marketing to avoid costly legal disputes.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Comprehensive Analysis:</strong> Search federal, state, and common law trademark databases for complete and thorough coverage.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Expert Assessment:</strong> Receive professional analysis of trademark search results with clear, actionable recommendations.</span>
                  </div>
                </div>
              </div>
              <div>
                <img
                  src={heroImage}
                  alt={service.title}
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 'file-ttab-petition' ? (
        <section className="py-12 lg:py-28 bg-white" data-testid="service-content">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl text-[#064E3B] mb-6">
                  What Our TTAB Petition Filing Service Covers
                </h2>
                <p className="text-slate-600 leading-relaxed mb-6">
                  Trademark Pact's TTAB petition filing service ensures your challenge or appeal is formally presented before the Trademark Trial and Appeal Board with complete accuracy, strategic arguments, and full procedural compliance.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Case Analysis:</strong> Identify and fully understand every legal ground available for your TTAB petition to develop a strong, well-targeted filing strategy from the very start.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Petition Preparation:</strong> Draft clear and persuasive petition arguments supported by solid evidence and legal precedent to formally present your strongest possible case before the board.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Expert Assessment:</strong> Receive professional analysis of your trademark dispute with clear, actionable recommendations on the most effective strategy to pursue before the board.</span>
                  </div>
                </div>
              </div>
              <div>
                <img
                  src={heroImage}
                  alt={service.title}
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 'trademark-ownership-transfers' ? (
        <section className="py-12 lg:py-28 bg-white" data-testid="service-content">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl text-[#064E3B] mb-6">
                  What Our Trademark Transfer Service Covers
                </h2>
                <p className="text-slate-600 leading-relaxed mb-6">
                  Trademark Pact's ownership transfer service ensures your trademark is legally and officially reassigned with complete documentation and government compliance. It provides a thorough handling of the assignment process across federal filings, supporting agreements, and recordation requirements. This protects the integrity of your brand asset and ensures uninterrupted legal ownership throughout every stage of the transfer.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Legal Documentation:</strong> Draft and review precise assignment agreements that fully comply with USPTO requirements and safeguard both parties.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Full Recordation Support:</strong> Handle all filings required to officially record the ownership change directly with the USPTO for maximum legal protection.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Expert Oversight:</strong> Receive professional guidance through every step of the transfer process with clear, actionable next steps.</span>
                  </div>
                </div>
              </div>
              <div>
                <img
                  src={heroImage}
                  alt={service.title}
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 'submission-of-statements-of-use' ? (
        <section className="py-12 lg:py-28 bg-white" data-testid="service-content">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl text-[#064E3B] mb-6">
                  What Our Statement of Use Service Covers
                </h2>
                <p className="text-slate-600 leading-relaxed mb-6">
                  Trademark Pact's statement of use service ensures your trademark application advances to full registration by confirming active commercial use with precise documentation and USPTO compliance. It provides thorough handling of the entire submission process across required specimens, supporting declarations, and filing deadlines. This protects the progress of your application and ensures uninterrupted movement toward official trademark registration at every stage.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Legal Documentation:</strong> Prepare and review accurate statements of use declarations that fully satisfy USPTO requirements and reflect your actual commercial activity.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Specimen Preparation:</strong> Identify and submit the right specimen showing real use of your trademark in commerce to meet all federal filing standards.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Expert Oversight:</strong> Receive professional guidance through every step of the submission process with clear, actionable next steps toward full registration.</span>
                  </div>
                </div>
              </div>
              <div>
                <img
                  src={heroImage}
                  alt={service.title}
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 'support-response-trademark-office-actions' ? (
        <section className="py-12 lg:py-28 bg-white" data-testid="service-content">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl text-[#064E3B] mb-6">
                  What Our Office Action Response Service Covers
                </h2>
                <p className="text-slate-600 leading-relaxed mb-6">
                  Trademark Pact's office action response service ensures your trademark application stays active and continues progressing toward full registration after receiving a USPTO refusal or requirement. It provides a thorough review of every examiner objection across legal grounds, evidence requirements, and procedural concerns. This helps craft targeted, persuasive arguments and accurately resolves every issue raised against your trademark application.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Refusal Analysis:</strong> Identify and fully understand every USPTO objection raised against your application to develop a strong, well-targeted response strategy.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Legal Argumentation:</strong> Prepare clear and persuasive arguments supported by solid evidence and legal precedent to directly overcome examiner refusals.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Expert Assessment:</strong> Receive professional analysis of your office action results with clear, actionable recommendations on the most effective path forward.</span>
                  </div>
                </div>
              </div>
              <div>
                <img
                  src={heroImage}
                  alt={service.title}
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 'register-your-copyright' ? (
        <section className="py-12 lg:py-28 bg-white" data-testid="service-content">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl text-[#064E3B] mb-6">
                  What Our Copyright Registration Service Includes
                </h2>
                <p className="text-slate-600 leading-relaxed mb-6">
                  Trademark Pact's copyright registration service guarantees your creative work is officially registered with the U.S. Copyright Office, backed by thorough documentation and strict adherence to legal standards.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Comprehensive Application Support:</strong> Prepare and submit detailed registration applications tailored to your specific work, whether it's literary, artistic, or digital content.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Accurate Documentation:</strong> Verify and organize all necessary materials and proof of originality to meet copyright office requirements.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Professional Guidance:</strong> Receive expert advice throughout the process with clear instructions and timely updates on your registration status.</span>
                  </div>
                </div>
              </div>
              <div>
                <img
                  src={heroImage}
                  alt={service.title}
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 'copyright-enforcement-assistance' ? (
        <section className="py-12 lg:py-28 bg-white" data-testid="service-content">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl text-[#064E3B] mb-6">
                  What Our Copyright Enforcement Service Includes
                </h2>
                <p className="text-slate-600 leading-relaxed mb-6">
                  Trademark Pact's copyright enforcement service offers a comprehensive solution to protect your copyrighted material from infringement. We oversee the enforcement process, including sending formal warnings, preparing legal documents, and monitoring usage to safeguard your rights and address violations swiftly.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Enforcement Documentation:</strong> Create and review effective legal notices and communications crafted to halt unauthorized use and preserve your ownership.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Ongoing Surveillance:</strong> Monitor multiple channels consistently to identify and respond to copyright breaches early.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Professional Legal Support:</strong> Benefit from expert guidance and clear instructions throughout every phase of the enforcement procedure.</span>
                  </div>
                </div>
              </div>
              <div>
                <img
                  src={heroImage}
                  alt={service.title}
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 'copyright-transfers-licensing' ? (
        <section className="py-12 lg:py-28 bg-white" data-testid="service-content">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl text-[#064E3B] mb-6">
                  What Our Copyright Transfers and Licensing Service Covers
                </h2>
                <p className="text-slate-600 leading-relaxed mb-6">
                  Trademark Pact's copyright transfers and licensing service delivers expert assistance in legally transferring ownership or granting usage rights of your copyrighted works.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Customized Agreements:</strong> Draft and review precise transfer and licensing contracts tailored to your specific needs, ensuring clarity and enforceability.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Legal Compliance Management:</strong> Navigate all regulatory requirements to guarantee your agreements are valid and recognized under copyright law.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Ongoing Support and Consultation:</strong> Receive professional advice and guidance throughout the negotiation and execution phases to safeguard your assets.</span>
                  </div>
                </div>
              </div>
              <div>
                <img
                  src={heroImage}
                  alt={service.title}
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 'legal-representation-copyright' ? (
        <section className="py-12 lg:py-28 bg-white" data-testid="service-content">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl text-[#064E3B] mb-6">
                  What Our Copyright Legal Representation Includes
                </h2>
                <p className="text-slate-600 leading-relaxed mb-6">
                  Our service covers all aspects of legal defense and enforcement, including negotiating settlements, litigating disputes, and preparing all necessary legal documents to protect your intellectual property rights effectively.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Legal Advocacy:</strong> Represent your interests in negotiations, mediations, or court proceedings with skilled copyright attorneys.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Strategic Legal Documentation:</strong> Draft and review all legal filings and correspondence to support your case and uphold your copyright claims.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Ongoing Case Management:</strong> Receive continuous legal guidance and updates as we navigate your copyright enforcement or defense process.</span>
                  </div>
                </div>
              </div>
              <div>
                <img
                  src={heroImage}
                  alt={service.title}
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 'copyright-portfolio-management' ? (
        <section className="py-12 lg:py-28 bg-white" data-testid="service-content">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl text-[#064E3B] mb-6">
                  What Our Copyright Portfolio Management Service Includes
                </h2>
                <p className="text-slate-600 leading-relaxed mb-6">
                  Trademark Pact's copyright portfolio management service offers a complete solution to oversee and safeguard your collection of copyrighted works. We provide systematic tracking, documentation, and strategic advice to help you maintain control and optimize the use of your intellectual property assets.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Portfolio Organization:</strong> Maintain detailed records and documentation for each copyrighted work ensuring clarity and easy access.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Rights Monitoring:</strong> Continuously track licenses, registrations, and usage to prevent unauthorized exploitation and ensure compliance.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Strategic Consultation:</strong> Receive expert guidance on managing, licensing, and protecting your copyright assets to maximize their value.</span>
                  </div>
                </div>
              </div>
              <div>
                <img
                  src={heroImage}
                  alt={service.title}
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 'dmca-compliance' ? (
        <section className="py-12 lg:py-28 bg-white" data-testid="service-content">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl text-[#064E3B] mb-6">
                  What Our DMCA Compliance Service Includes
                </h2>
                <p className="text-slate-600 leading-relaxed mb-6">
                  Trademark Pact's DMCA compliance service delivers a thorough approach to managing and enforcing your rights under the Digital Millennium Copyright Act. We handle the entire process of preparing takedown notices, responding to counter-notices, and monitoring content use across online platforms to protect your digital intellectual property effectively.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Takedown Notice Preparation:</strong> Draft and review legally sound DMCA takedown requests to promptly remove unauthorized content.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Counter-Notice Management:</strong> Assist in responding to counter-claims with accurate documentation to uphold your rights.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Continuous Monitoring:</strong> Track online platforms consistently to detect and address potential copyright infringements early.</span>
                  </div>
                </div>
              </div>
              <div>
                <img
                  src={heroImage}
                  alt={service.title}
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 'business-immigration-law' ? (
        <section className="py-12 lg:py-28 bg-white" data-testid="service-content">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl text-[#064E3B] mb-6">
                  What Our Business Immigration Law Service Includes
                </h2>
                <p className="text-slate-600 leading-relaxed mb-6">
                  Trademark Pact's business immigration law service offers comprehensive support for companies and individuals navigating employment-based visas, compliance, and immigration-related challenges. We handle visa applications, regulatory compliance, and legal representation to ensure smooth immigration processes for your workforce.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Visa Application Assistance:</strong> Prepare and file employment-based visa petitions and supporting documentation tailored to your business needs.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Regulatory Compliance:</strong> Guide you through maintaining compliance with immigration laws and labor regulations to avoid costly penalties.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Legal Representation:</strong> Provide expert counsel and advocacy in immigration matters, including audits and legal challenges.</span>
                  </div>
                </div>
              </div>
              <div>
                <img
                  src={heroImage}
                  alt={service.title}
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 'due-diligence-process' ? (
        <section className="py-12 lg:py-28 bg-white" data-testid="service-content">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl text-[#064E3B] mb-6">
                  What Our Due Diligence Process Includes
                </h2>
                <p className="text-slate-600 leading-relaxed mb-6">
                  Trademark Pact's due diligence process provides a detailed examination of legal, financial, and intellectual property aspects to ensure informed decision-making. We conduct comprehensive investigations, verify compliance, and assess potential risks to protect your investments and business transactions.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Comprehensive Analysis:</strong> Perform in-depth reviews of contracts, trademarks, copyrights, and other critical assets.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Risk Assessment:</strong> Identify legal, financial, and operational risks that could impact your business goals.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Clear Reporting:</strong> Deliver detailed reports with actionable recommendations to guide your strategic decisions.</span>
                  </div>
                </div>
              </div>
              <div>
                <img
                  src={heroImage}
                  alt={service.title}
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 'it-commercial-law' ? (
        <section className="py-12 lg:py-28 bg-white" data-testid="service-content">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl text-[#064E3B] mb-6">
                  What Our IT Commercial Law Service Covers
                </h2>
                <p className="text-slate-600 leading-relaxed mb-6">
                  Trademark Pact's IT commercial law service provides comprehensive legal support tailored to the technology sector. We assist with drafting and reviewing software licenses, service agreements, data protection policies, and ensure compliance with industry regulations to safeguard your business operations.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Contract Drafting and Review:</strong> Prepare and examine tailored agreements including software licenses, SaaS contracts, and vendor agreements.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Regulatory Compliance:</strong> Guide your business through complex data privacy laws and IT regulations to avoid legal pitfalls.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Legal Consultation:</strong> Offer expert advice on dispute resolution, liability issues, and intellectual property matters related to IT commerce.</span>
                  </div>
                </div>
              </div>
              <div>
                <img
                  src={heroImage}
                  alt={service.title}
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 'partnerships-llps' ? (
        <section className="py-12 lg:py-28 bg-white" data-testid="service-content">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl text-[#064E3B] mb-6">
                  What Our Partnerships and LLP Service Includes
                </h2>
                <p className="text-slate-600 leading-relaxed mb-6">
                  Trademark Pact offers comprehensive legal support for forming and managing partnerships and LLPs. We assist with drafting partnership agreements, ensuring regulatory compliance, and providing ongoing legal counsel to safeguard your business interests and maintain strong partnerships.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Agreement Drafting:</strong> Create and review customized partnership and LLP agreements that clearly define roles, responsibilities, and profit sharing.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Regulatory Compliance:</strong> Navigate local and federal laws to ensure your partnership or LLP meets all legal requirements.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Ongoing Legal Support:</strong> Provide continuous guidance on dispute resolution, amendments, and compliance matters to keep your business on track.</span>
                  </div>
                </div>
              </div>
              <div>
                <img
                  src={heroImage}
                  alt={service.title}
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 'data-protection-privacy-law' ? (
        <section className="py-12 lg:py-28 bg-white" data-testid="service-content">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl text-[#064E3B] mb-6">
                  What Our Data Protection and Privacy Law Service Covers
                </h2>
                <p className="text-slate-600 leading-relaxed mb-6">
                  Trademark Pact offers thorough support for businesses navigating complex data privacy regulations such as GDPR, CCPA, and others. We assist with policy drafting, compliance audits, breach response planning, and ongoing legal advice to protect your data and reputation.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Privacy Policy Drafting:</strong> Develop clear and compliant privacy policies tailored to your business practices and regulatory requirements.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Compliance Audits:</strong> Conduct detailed assessments to identify gaps and implement measures aligning with applicable data protection laws.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Breach Response Planning:</strong> Prepare legal strategies and protocols to respond swiftly and effectively to data breaches or privacy incidents.</span>
                  </div>
                </div>
              </div>
              <div>
                <img
                  src={heroImage}
                  alt={service.title}
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 'limited-liability-company' ? (
        <section className="py-12 lg:py-28 bg-white" data-testid="service-content">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl text-[#064E3B] mb-6">
                  What Our Limited Liability Company Service Includes
                </h2>
                <p className="text-slate-600 leading-relaxed mb-6">
                  Trademark Pact offers comprehensive assistance in establishing and maintaining LLCs. We guide you through the entire process, from drafting operating agreements to ensuring compliance with state and federal regulations, helping you build a solid foundation for your business.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Operating Agreement Drafting:</strong> Prepare customized agreements that define management structures, member roles, and profit distribution.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Regulatory Compliance:</strong> Navigate all legal requirements to keep your LLC in good standing and avoid penalties.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Ongoing Legal Support:</strong> Provide continuous guidance on amendments, filings, and dispute resolution to support your business growth.</span>
                  </div>
                </div>
              </div>
              <div>
                <img
                  src={heroImage}
                  alt={service.title}
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 'c-corporation' ? (
        <section className="py-12 lg:py-28 bg-white" data-testid="service-content">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl text-[#064E3B] mb-6">
                  What Our C Corporation Service Includes
                </h2>
                <p className="text-slate-600 leading-relaxed mb-6">
                  Trademark Pact provides comprehensive support for forming and maintaining C Corporations. We assist with drafting corporate bylaws, filing incorporation documents, and ensuring your company meets all federal and state regulatory requirements for smooth operations.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Bylaws Preparation:</strong> Develop detailed corporate bylaws that define governance, shareholder rights, and management responsibilities.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Incorporation Filing:</strong> Handle all necessary paperwork to officially register your C Corporation with the appropriate government agencies.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Ongoing Compliance Support:</strong> Offer continuous legal advice and assistance with filings, shareholder meetings, and regulatory obligations.</span>
                  </div>
                </div>
              </div>
              <div>
                <img
                  src={heroImage}
                  alt={service.title}
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 's-corporation' ? (
        <section className="py-12 lg:py-28 bg-white" data-testid="service-content">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl text-[#064E3B] mb-6">
                  What Our S Corporation Service Includes
                </h2>
                <p className="text-slate-600 leading-relaxed mb-6">
                  Trademark Pact provides comprehensive guidance for establishing and maintaining S Corporations. We assist with preparing necessary election documents, drafting corporate bylaws, and ensuring compliance with IRS and state regulations to help your business operate smoothly and efficiently.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Election Filing Assistance:</strong> Prepare and submit IRS Form 2553 to secure your S Corporation tax status.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Bylaws and Agreements:</strong> Draft detailed corporate bylaws and shareholder agreements that govern your company's operations.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Compliance Management:</strong> Provide ongoing support to meet federal and state filing requirements and maintain good standing.</span>
                  </div>
                </div>
              </div>
              <div>
                <img
                  src={heroImage}
                  alt={service.title}
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 'non-profit' ? (
        <section className="py-12 lg:py-28 bg-white" data-testid="service-content">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl text-[#064E3B] mb-6">
                  What Our Non-Profit Service Includes
                </h2>
                <p className="text-slate-600 leading-relaxed mb-6">
                  Trademark Pact offers comprehensive assistance in creating and maintaining non-profit entities. We guide you through drafting bylaws, filing for tax-exempt status, and ensuring compliance with federal and state regulations so your organization can thrive legally and operationally.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Bylaws and Governance Documents:</strong> Prepare customized bylaws and policies that establish your organization's structure and operational rules.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Tax-Exempt Status Filing:</strong> Assist with preparing and submitting IRS Form 1023 or 1024 to obtain federal tax-exempt recognition.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Compliance Support:</strong> Provide ongoing legal advice to maintain your non-profit's good standing with regulatory bodies and donors.</span>
                  </div>
                </div>
              </div>
              <div>
                <img
                  src={heroImage}
                  alt={service.title}
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 'search-existing-patent' ? (
        <section className="py-12 lg:py-28 bg-white" data-testid="service-content">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl text-[#064E3B] mb-6">
                  What Our Existing Patent Search Service Includes
                </h2>
                <p className="text-slate-600 leading-relaxed mb-6">
                  Trademark Pact's patent search service provides an indepth review of patent databases to identify existing patents that may impact your invention. We conduct detailed searches, analyze relevant patents, and deliver clear reports to help you make informed decisions about patentability and potential risks.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Extensive Patent Database Research:</strong> Search national and international patent records to uncover relevant existing patents.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Detailed Patent Analysis:</strong> Assess identified patents to determine their relevance and impact on your invention's novelty.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Clear Reporting and Guidance:</strong> Provide comprehensive search reports along with expert advice on next steps and patent strategy.</span>
                  </div>
                </div>
              </div>
              <div>
                <img
                  src={heroImage}
                  alt={service.title}
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 'patent-office-actions-support' ? (
        <section className="py-12 lg:py-28 bg-white" data-testid="service-content">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl text-[#064E3B] mb-6">
                  What Our Patent Office Actions Support Includes
                </h2>
                <p className="text-slate-600 leading-relaxed mb-6">
                  Trademark Pact's patent office actions support service assists inventors and businesses in responding effectively to examiner communications during the patent prosecution process. We analyze office actions, prepare detailed responses, and file necessary documents to keep your patent application on track.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Comprehensive Action Review:</strong> Carefully examine office actions issued by patent examiners for accuracy and strategy.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Strategic Response Preparation:</strong> Draft well-reasoned and compliant replies to address examiner objections and requirements.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Timely Filing and Follow-Up:</strong> Ensure all responses and documents are submitted within deadlines to avoid application abandonment.</span>
                  </div>
                </div>
              </div>
              <div>
                <img
                  src={heroImage}
                  alt={service.title}
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 'patent-application-submission' ? (
        <section className="py-12 lg:py-28 bg-white" data-testid="service-content">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl text-[#064E3B] mb-6">
                  What Our Patent Application Submission Service Includes
                </h2>
                <p className="text-slate-600 leading-relaxed mb-6">
                  Trademark Pact offers comprehensive support to prepare and file your patent applications accurately and efficiently. We ensure all documentation meets legal standards, conduct thorough reviews, and handle the submission process to the appropriate patent offices for timely protection of your invention.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Application Preparation:</strong> Draft and organize detailed patent applications tailored to your invention and legal requirements.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Document Review:</strong> Conduct rigorous checks to ensure completeness, accuracy, and compliance with patent office guidelines.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Filing and Follow-Up:</strong> Manage the official submission process and monitor your application's status for smooth progression.</span>
                  </div>
                </div>
              </div>
              <div>
                <img
                  src={heroImage}
                  alt={service.title}
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 'legal-representation-patent' ? (
        <section className="py-12 lg:py-28 bg-white" data-testid="service-content">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl text-[#064E3B] mb-6">
                  What Our Legal Representation for Patent Protection Includes
                </h2>
                <p className="text-slate-600 leading-relaxed mb-6">
                  Trademark Pact offers comprehensive legal support to safeguard your patent rights. We represent you in enforcement actions, negotiate licensing agreements, respond to infringement claims, and guide you through legal challenges to ensure your intellectual property remains secure.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Patent Enforcement Advocacy:</strong> Advocate on your behalf in disputes involving patent infringement and unauthorized use.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Contract and Licensing Negotiations:</strong> Draft and review licensing agreements to maximize the value and protection of your patents.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Legal Strategy and Defense:</strong> Provide expert counsel and representation in litigation, oppositions, and appeals related to your patents.</span>
                  </div>
                </div>
              </div>
              <div>
                <img
                  src={heroImage}
                  alt={service.title}
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 'amazon-brand-registry' ? (
        <section className="py-12 lg:py-28 bg-white" data-testid="service-content">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl text-[#064E3B] mb-6">
                  What Our Amazon Brand Registry Service Includes
                </h2>
                <p className="text-slate-600 leading-relaxed mb-6">
                  Trademark Pact's Amazon Brand Registry service guides you through the entire enrollment process, ensuring your brand meets Amazon's requirements for trademark registration. We help you secure your brand's presence, protect against counterfeiters, and gain access to enhanced marketing tools.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Brand Enrollment Assistance:</strong> Prepare and submit all necessary trademark documentation to register your brand with Amazon.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Brand Protection Guidance:</strong> Implement strategies to monitor and prevent unauthorized sellers and counterfeit products.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Ongoing Support:</strong> Provide expert advice and help you leverage Amazon's brand protection features for sustained security.</span>
                  </div>
                </div>
              </div>
              <div>
                <img
                  src={heroImage}
                  alt={service.title}
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 'bookkeeping-service' ? (
        <section className="py-12 lg:py-28 bg-white" data-testid="service-content">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl text-[#064E3B] mb-6">
                  What Our Bookkeeping Service Includes
                </h2>
                <p className="text-slate-600 leading-relaxed mb-6">
                  Trademark Pact's bookkeeping service offers comprehensive management of your financial transactions, ensuring precise recordkeeping and timely reporting. We handle everything from daily entries to reconciliations, helping you maintain clear and compliant financial records.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Transaction Recording:</strong> Accurately document all financial transactions including sales, purchases, and payments.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Account Reconciliation:</strong> Regularly reconcile accounts to ensure consistency and identify discrepancies.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Financial Reporting:</strong> Provide detailed reports to give you clear insights into your business's financial health.</span>
                  </div>
                </div>
              </div>
              <div>
                <img
                  src={heroImage}
                  alt={service.title}
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 'business-formation-services' ? (
        <section className="py-12 lg:py-28 bg-white" data-testid="service-content">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl text-[#064E3B] mb-6">
                  What Our Business Formation Services Include
                </h2>
                <p className="text-slate-600 leading-relaxed mb-6">
                  Trademark Pact offers comprehensive assistance to launch your business smoothly and legally. We help you choose the right business structure, prepare essential formation documents, and ensure compliance with state and federal regulations for a strong foundation.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Entity Selection Guidance:</strong> Advise on the best legal structure—LLC, corporation, partnership, or sole proprietorship—based on your goals.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Document Preparation:</strong> Draft and file formation documents, including articles of incorporation or organization, with the appropriate authorities.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Compliance Support:</strong> Assist with registrations, tax IDs, and ongoing filing requirements to keep your business in good standing.</span>
                  </div>
                </div>
              </div>
              <div>
                <img
                  src={heroImage}
                  alt={service.title}
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 'certificate-good-standing' ? (
        <section className="py-12 lg:py-28 bg-white" data-testid="service-content">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl text-[#064E3B] mb-6">
                  What Our Certificate of Good Standing Service Includes
                </h2>
                <p className="text-slate-600 leading-relaxed mb-6">
                  Trademark Pact offers comprehensive assistance in securing your certificate of good standing from the appropriate state authorities. We verify your company's compliance with all necessary filings, prepare and submit the required documents, and ensure prompt delivery of your certificate to support your business needs.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Compliance Verification:</strong> Confirm that your business meets all state filing and regulatory requirements to maintain good standing.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Document Preparation and Filing:</strong> Accurately prepare and submit applications to obtain your certificate without delay.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Follow-Up and Expedited Processing:</strong> Manage communications with state agencies to expedite processing and delivery of your certificate.</span>
                  </div>
                </div>
              </div>
              <div>
                <img
                  src={heroImage}
                  alt={service.title}
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 'company-dissolution' ? (
        <section className="py-12 lg:py-28 bg-white" data-testid="service-content">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl text-[#064E3B] mb-6">
                  What Our Company Dissolution Service Includes
                </h2>
                <p className="text-slate-600 leading-relaxed mb-6">
                  Trademark Pact offers comprehensive assistance to ensure your business is properly and legally dissolved. We handle all necessary filings, notify relevant agencies, and manage compliance requirements to help you close your company smoothly and avoid future liabilities.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Legal Documentation Preparation:</strong> Draft and file dissolution documents in accordance with state and federal laws.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Regulatory Compliance:</strong> Manage notifications to tax authorities, creditors, and other stakeholders to ensure proper closure.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Ongoing Support:</strong> Provide guidance on final tax filings, asset distribution, and resolving outstanding obligations.</span>
                  </div>
                </div>
              </div>
              <div>
                <img
                  src={heroImage}
                  alt={service.title}
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 'company-name-change' ? (
        <section className="py-12 lg:py-28 bg-white" data-testid="service-content">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl text-[#064E3B] mb-6">
                  What Our Company Name Change Service Includes
                </h2>
                <p className="text-slate-600 leading-relaxed mb-6">
                  Trademark Pact provides comprehensive support for legally changing your company name. We prepare and file the necessary documents, update registrations with state and federal agencies, and ensure all legal requirements are met for a smooth transition to your new business identity.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Name Availability Check:</strong> Verify that your desired new name is available and complies with state naming regulations.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Document Preparation and Filing:</strong> Prepare and file articles of amendment and other required documents with state authorities.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700"><strong>Comprehensive Updates:</strong> Update your business name across tax registrations, licenses, and other official records.</span>
                  </div>
                </div>
              </div>
              <div>
                <img
                  src={heroImage}
                  alt={service.title}
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : (
        <section className="py-12 lg:py-28 bg-white" data-testid="service-content">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl text-[#064E3B] mb-6">
                  About This Service
                </h2>
                <p className="text-slate-600 leading-relaxed mb-6">
                  {service.description}
                </p>
                <div className="space-y-3">
                  {(service.benefits || []).slice(0, 3).map((b, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                      <span className="text-sm text-slate-700"><strong>{b.title}:</strong> {b.desc}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <img
                  src={heroImage}
                  alt={service.title}
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* 3. Benefits */}
      {slug === 'active-monitoring-trademark-infringement' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-benefits">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-4">
                Why Choose Our Service
              </h2>
            </div>
            <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-0">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Early Detection</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Identify potential infringements and conflicting filings as early as possible to prevent unauthorized use from weakening your trademark rights and brand value.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-1">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Thorough Coverage</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Conduct continuous monitoring across trademark databases, e-commerce platforms, and online channels to ensure no violation goes unnoticed.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-2">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Expert Guidance</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Receive detailed professional analysis of every detected threat with clear, actionable recommendations tailored to your specific trademark and industry.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-3">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Cost Savings</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Prevent expensive litigation and brand damage by catching infringement at the earliest stage before unauthorized use becomes deeply established.</p>
              </div>
            </div>
            <div className="md:hidden relative px-8">
              <Carousel opts={{ align: 'start', loop: true }}>
                <CarouselContent className="-ml-4">
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Early Detection</h3>
                      <p className="text-sm text-slate-600">Identify potential infringements and conflicting filings as early as possible to prevent unauthorized use from weakening your trademark rights and brand value.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Thorough Coverage</h3>
                      <p className="text-sm text-slate-600">Conduct continuous monitoring across trademark databases, e-commerce platforms, and online channels to ensure no violation goes unnoticed.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Expert Guidance</h3>
                      <p className="text-sm text-slate-600">Receive detailed professional analysis of every detected threat with clear, actionable recommendations tailored to your specific trademark and industry.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Cost Savings</h3>
                      <p className="text-sm text-slate-600">Prevent expensive litigation and brand damage by catching infringement at the earliest stage before unauthorized use becomes deeply established.</p>
                    </div>
                  </CarouselItem>
                </CarouselContent>
                <CarouselPrevious className="border-emerald-200 text-[#064E3B]" />
                <CarouselNext className="border-emerald-200 text-[#064E3B]" />
              </Carousel>
            </div>
          </div>
        </section>
      ) : slug === 'clearance-search-services' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-benefits">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-4">
                Why Choose Our Service
              </h2>
            </div>
            <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-0">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Risk Reduction</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Identify possible conflicts early on to avoid costly mistakes before investing in branding and marketing.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-1">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Extensive Evaluation</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Conduct thorough searches across federal, state, and common law databases for complete trademark coverage.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-2">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Expert Evaluation</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Receive detailed professional analysis of search results with clear, actionable recommendations.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-3">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Cost Savings</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Prevent expensive rebranding by detecting conflicts at the earliest stage of your brand development.</p>
              </div>
            </div>
            <div className="md:hidden relative px-8">
              <Carousel opts={{ align: 'start', loop: true }}>
                <CarouselContent className="-ml-4">
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Risk Reduction</h3>
                      <p className="text-sm text-slate-600">Identify possible conflicts early on to avoid costly mistakes before investing in branding and marketing.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Extensive Evaluation</h3>
                      <p className="text-sm text-slate-600">Conduct thorough searches across federal, state, and common law databases for complete trademark coverage.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Expert Evaluation</h3>
                      <p className="text-sm text-slate-600">Receive detailed professional analysis of search results with clear, actionable recommendations.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Cost Savings</h3>
                      <p className="text-sm text-slate-600">Prevent expensive rebranding by detecting conflicts at the earliest stage of your brand development.</p>
                    </div>
                  </CarouselItem>
                </CarouselContent>
                <CarouselPrevious className="border-emerald-200 text-[#064E3B]" />
                <CarouselNext className="border-emerald-200 text-[#064E3B]" />
              </Carousel>
            </div>
          </div>
        </section>
      ) : slug === 'file-ttab-petition' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-benefits">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-4">
                Why Choose Our Service
              </h2>
            </div>
            <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-0">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Strategic Filing</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Ensure your TTAB petition is built on solid legal grounds and submitted correctly the first time to avoid procedural errors that could weaken or dismiss your case.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-1">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Thorough Preparation</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Conduct a complete review of all relevant legal grounds, prior registrations, and supporting evidence to ensure nothing critical is overlooked in your petition.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-2">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Expert Guidance</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Receive detailed professional analysis of your specific dispute with clear, actionable recommendations tailored to your unique trademark circumstances and business goals.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-3">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Cost Savings</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Prevent unnecessary legal expenses and protect your trademark investment by approaching board proceedings accurately and strategically from the earliest possible stage.</p>
              </div>
            </div>
            <div className="md:hidden relative px-8">
              <Carousel opts={{ align: 'start', loop: true }}>
                <CarouselContent className="-ml-4">
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Strategic Filing</h3>
                      <p className="text-sm text-slate-600">Ensure your TTAB petition is built on solid legal grounds and submitted correctly the first time to avoid procedural errors that could weaken or dismiss your case.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Thorough Preparation</h3>
                      <p className="text-sm text-slate-600">Conduct a complete review of all relevant legal grounds, prior registrations, and supporting evidence to ensure nothing critical is overlooked in your petition.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Expert Guidance</h3>
                      <p className="text-sm text-slate-600">Receive detailed professional analysis of your specific dispute with clear, actionable recommendations tailored to your unique trademark circumstances and business goals.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Cost Savings</h3>
                      <p className="text-sm text-slate-600">Prevent unnecessary legal expenses and protect your trademark investment by approaching board proceedings accurately and strategically from the earliest possible stage.</p>
                    </div>
                  </CarouselItem>
                </CarouselContent>
                <CarouselPrevious className="border-emerald-200 text-[#064E3B]" />
                <CarouselNext className="border-emerald-200 text-[#064E3B]" />
              </Carousel>
            </div>
          </div>
        </section>
      ) : slug === 'trademark-ownership-transfers' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-benefits">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-4">
                Why Choose Our Service
              </h2>
            </div>
            <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-0">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Airtight Transfers</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Ensure your trademark assignment is legally valid and enforceable, eliminating gaps that could jeopardize your brand ownership down the line.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-1">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">End-to-End Management</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Trademark Pact handles everything from drafting the assignment agreement to completing the official USPTO recordation on your behalf.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-2">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Expert Oversight</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Receive detailed professional guidance at every stage of the transfer with clear recommendations tailored to your specific situation.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-3">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Risk Prevention</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Avoid ownership disputes, licensing complications, and registration lapses by executing the transfer correctly from the very start.</p>
              </div>
            </div>
            <div className="md:hidden relative px-8">
              <Carousel opts={{ align: 'start', loop: true }}>
                <CarouselContent className="-ml-4">
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Airtight Transfers</h3>
                      <p className="text-sm text-slate-600">Ensure your trademark assignment is legally valid and enforceable, eliminating gaps that could jeopardize your brand ownership down the line.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">End-to-End Management</h3>
                      <p className="text-sm text-slate-600">Trademark Pact handles everything from drafting the assignment agreement to completing the official USPTO recordation on your behalf.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Expert Oversight</h3>
                      <p className="text-sm text-slate-600">Receive detailed professional guidance at every stage of the transfer with clear recommendations tailored to your specific situation.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Risk Prevention</h3>
                      <p className="text-sm text-slate-600">Avoid ownership disputes, licensing complications, and registration lapses by executing the transfer correctly from the very start.</p>
                    </div>
                  </CarouselItem>
                </CarouselContent>
                <CarouselPrevious className="border-emerald-200 text-[#064E3B]" />
                <CarouselNext className="border-emerald-200 text-[#064E3B]" />
              </Carousel>
            </div>
          </div>
        </section>
      ) : slug === 'submission-of-statements-of-use' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-benefits">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-4">
                Why Choose Our Service
              </h2>
            </div>
            <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-0">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Deadline Protection</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Ensure your statement of use is filed accurately and on time, eliminating the risk of abandonment that could cost you your trademark registration entirely.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-1">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">End-to-End Management</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Trademark Pact handles everything from preparing your declaration to selecting the correct specimen and completing the official USPTO submission on your behalf.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-2">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Expert Oversight</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Receive detailed professional guidance at every stage of the filing with clear recommendations tailored to your specific application and business activity.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-3">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Risk Prevention</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Avoid application abandonment, office actions, and registration delays by submitting your statement of use correctly from the very first attempt.</p>
              </div>
            </div>
            <div className="md:hidden relative px-8">
              <Carousel opts={{ align: 'start', loop: true }}>
                <CarouselContent className="-ml-4">
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Deadline Protection</h3>
                      <p className="text-sm text-slate-600">Ensure your statement of use is filed accurately and on time, eliminating the risk of abandonment that could cost you your trademark registration entirely.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">End-to-End Management</h3>
                      <p className="text-sm text-slate-600">Trademark Pact handles everything from preparing your declaration to selecting the correct specimen and completing the official USPTO submission on your behalf.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Expert Oversight</h3>
                      <p className="text-sm text-slate-600">Receive detailed professional guidance at every stage of the filing with clear recommendations tailored to your specific application and business activity.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Risk Prevention</h3>
                      <p className="text-sm text-slate-600">Avoid application abandonment, office actions, and registration delays by submitting your statement of use correctly from the very first attempt.</p>
                    </div>
                  </CarouselItem>
                </CarouselContent>
                <CarouselPrevious className="border-emerald-200 text-[#064E3B]" />
                <CarouselNext className="border-emerald-200 text-[#064E3B]" />
              </Carousel>
            </div>
          </div>
        </section>
      ) : slug === 'support-response-trademark-office-actions' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-benefits">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-4">
                Why Choose Our Service
              </h2>
            </div>
            <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-0">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Deadline Protection</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Identify and respond to every USPTO requirement accurately and on time to avoid costly application abandonment and permanently losing your trademark rights.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-1">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Thorough Review</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Conduct a complete analysis of every refusal ground across procedural, substantive, and evidentiary issues to ensure nothing is overlooked in your response.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-2">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Expert Guidance</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Receive detailed professional analysis of your specific office action with clear, actionable recommendations tailored to your unique application circumstances.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-3">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Cost Savings</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Prevent permanent application loss and avoid expensive refiling fees by addressing all USPTO concerns correctly at the earliest stage of the process.</p>
              </div>
            </div>
            <div className="md:hidden relative px-8">
              <Carousel opts={{ align: 'start', loop: true }}>
                <CarouselContent className="-ml-4">
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Deadline Protection</h3>
                      <p className="text-sm text-slate-600">Identify and respond to every USPTO requirement accurately and on time to avoid costly application abandonment and permanently losing your trademark rights.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Thorough Review</h3>
                      <p className="text-sm text-slate-600">Conduct a complete analysis of every refusal ground across procedural, substantive, and evidentiary issues to ensure nothing is overlooked in your response.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Expert Guidance</h3>
                      <p className="text-sm text-slate-600">Receive detailed professional analysis of your specific office action with clear, actionable recommendations tailored to your unique application circumstances.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Cost Savings</h3>
                      <p className="text-sm text-slate-600">Prevent permanent application loss and avoid expensive refiling fees by addressing all USPTO concerns correctly at the earliest stage of the process.</p>
                    </div>
                  </CarouselItem>
                </CarouselContent>
                <CarouselPrevious className="border-emerald-200 text-[#064E3B]" />
                <CarouselNext className="border-emerald-200 text-[#064E3B]" />
              </Carousel>
            </div>
          </div>
        </section>
      ) : slug === 'register-your-copyright' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-benefits">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-4">
                Why Choose Trademark Pact for Copyright Registration
              </h2>
            </div>
            <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-0">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Reliable Protection</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Secure your creative assets with a legally recognized copyright that establishes your exclusive rights and prevents unauthorized use.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-1">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Complete Process Handling</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Trademark Pact manages every step, from gathering materials to filing the official application, ensuring a hassle-free experience.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-2">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Expert Support</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Benefit from knowledgeable assistance designed to simplify copyright law and keep you informed.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-3">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Risk Reduction</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Avoid common application errors and delays that could compromise your copyright protection or registration timeline.</p>
              </div>
            </div>
            <div className="md:hidden relative px-8">
              <Carousel opts={{ align: 'start', loop: true }}>
                <CarouselContent className="-ml-4">
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Reliable Protection</h3>
                      <p className="text-sm text-slate-600">Secure your creative assets with a legally recognized copyright that establishes your exclusive rights and prevents unauthorized use.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Complete Process Handling</h3>
                      <p className="text-sm text-slate-600">Trademark Pact manages every step, from gathering materials to filing the official application, ensuring a hassle-free experience.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Expert Support</h3>
                      <p className="text-sm text-slate-600">Benefit from knowledgeable assistance designed to simplify copyright law and keep you informed.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Risk Reduction</h3>
                      <p className="text-sm text-slate-600">Avoid common application errors and delays that could compromise your copyright protection or registration timeline.</p>
                    </div>
                  </CarouselItem>
                </CarouselContent>
                <CarouselPrevious className="border-emerald-200 text-[#064E3B]" />
                <CarouselNext className="border-emerald-200 text-[#064E3B]" />
              </Carousel>
            </div>
          </div>
        </section>
      ) : slug === 'copyright-enforcement-assistance' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-benefits">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-4">
                Why Trust Us for Copyright Enforcement
              </h2>
            </div>
            <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-0">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Thorough Protection</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Defend your copyrights with aggressive and legally sound enforcement measures.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-1">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Complete Assistance</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Trademark Pact manages all enforcement activities from drafting notices to advising on further legal strategies, ensuring comprehensive support.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-2">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Expertise You Can Rely On</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Gain access to specialized legal counsel tailored to your copyright enforcement challenges.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-3">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Damage Control</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Stop unauthorized use quickly to protect the value and reputation of your creative assets.</p>
              </div>
            </div>
            <div className="md:hidden relative px-8">
              <Carousel opts={{ align: 'start', loop: true }}>
                <CarouselContent className="-ml-4">
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Thorough Protection</h3>
                      <p className="text-sm text-slate-600">Defend your copyrights with aggressive and legally sound enforcement measures.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Complete Assistance</h3>
                      <p className="text-sm text-slate-600">Trademark Pact manages all enforcement activities from drafting notices to advising on further legal strategies, ensuring comprehensive support.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Expertise You Can Rely On</h3>
                      <p className="text-sm text-slate-600">Gain access to specialized legal counsel tailored to your copyright enforcement challenges.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Damage Control</h3>
                      <p className="text-sm text-slate-600">Stop unauthorized use quickly to protect the value and reputation of your creative assets.</p>
                    </div>
                  </CarouselItem>
                </CarouselContent>
                <CarouselPrevious className="border-emerald-200 text-[#064E3B]" />
                <CarouselNext className="border-emerald-200 text-[#064E3B]" />
              </Carousel>
            </div>
          </div>
        </section>
      ) : slug === 'copyright-transfers-licensing' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-benefits">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-4">
                Why Choose Trademark Pact for Transfers and Licensing
              </h2>
            </div>
            <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-0">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Clear Legal Frameworks</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Ensure your copyright transfers and licenses are backed by solid, legally binding agreements that prevent disputes.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-1">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Full-Service Handling</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Trademark Pact manages all documentation, negotiations, and compliance checks, making the process seamless and worry-free.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-2">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Specialized Expertise</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Benefit from legal professionals experienced in copyright law who customize solutions to your unique situation.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-3">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Protect Your Interests</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Avoid costly misunderstandings and protect your intellectual property rights through expertly crafted contracts.</p>
              </div>
            </div>
            <div className="md:hidden relative px-8">
              <Carousel opts={{ align: 'start', loop: true }}>
                <CarouselContent className="-ml-4">
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Clear Legal Frameworks</h3>
                      <p className="text-sm text-slate-600">Ensure your copyright transfers and licenses are backed by solid, legally binding agreements that prevent disputes.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Full-Service Handling</h3>
                      <p className="text-sm text-slate-600">Trademark Pact manages all documentation, negotiations, and compliance checks, making the process seamless and worry-free.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Specialized Expertise</h3>
                      <p className="text-sm text-slate-600">Benefit from legal professionals experienced in copyright law who customize solutions to your unique situation.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Protect Your Interests</h3>
                      <p className="text-sm text-slate-600">Avoid costly misunderstandings and protect your intellectual property rights through expertly crafted contracts.</p>
                    </div>
                  </CarouselItem>
                </CarouselContent>
                <CarouselPrevious className="border-emerald-200 text-[#064E3B]" />
                <CarouselNext className="border-emerald-200 text-[#064E3B]" />
              </Carousel>
            </div>
          </div>
        </section>
      ) : slug === 'legal-representation-copyright' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-benefits">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-4">
                Why Choose Trademark Pact for Legal Representation
              </h2>
            </div>
            <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-0">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Strong Legal Defense</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Protect your copyrights with experienced attorneys who fight vigorously to uphold your rights.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-1">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Full-Service Legal Support</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Trademark Pact handles every stage of the legal process, from dispute resolution to litigation, providing comprehensive representation.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-2">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Trusted Expertise</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Work with copyright law specialists who tailor their approach to your unique situation and legal needs.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-3">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Risk Mitigation</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Avoid costly legal pitfalls and strengthen your position with professional counsel guiding every step of your case.</p>
              </div>
            </div>
            <div className="md:hidden relative px-8">
              <Carousel opts={{ align: 'start', loop: true }}>
                <CarouselContent className="-ml-4">
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Strong Legal Defense</h3>
                      <p className="text-sm text-slate-600">Protect your copyrights with experienced attorneys who fight vigorously to uphold your rights.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Full-Service Legal Support</h3>
                      <p className="text-sm text-slate-600">Trademark Pact handles every stage of the legal process, from dispute resolution to litigation, providing comprehensive representation.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Trusted Expertise</h3>
                      <p className="text-sm text-slate-600">Work with copyright law specialists who tailor their approach to your unique situation and legal needs.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Risk Mitigation</h3>
                      <p className="text-sm text-slate-600">Avoid costly legal pitfalls and strengthen your position with professional counsel guiding every step of your case.</p>
                    </div>
                  </CarouselItem>
                </CarouselContent>
                <CarouselPrevious className="border-emerald-200 text-[#064E3B]" />
                <CarouselNext className="border-emerald-200 text-[#064E3B]" />
              </Carousel>
            </div>
          </div>
        </section>
      ) : slug === 'copyright-portfolio-management' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-benefits">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-4">
                Why Choose Trademark Pact for Portfolio Management
              </h2>
            </div>
            <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-0">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Complete Oversight</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Keep your entire copyright portfolio organized and protected under expert management.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-1">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Proactive Monitoring</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Trademark Pact actively monitors your assets to detect unauthorized use early and maintain compliance.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-2">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Tailored Expertise</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Benefit from personalized strategies designed to fit your unique portfolio and business goals.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-3">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Maximize Asset Value</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Enhance the commercial potential of your copyrights through strategic licensing and protection measures.</p>
              </div>
            </div>
            <div className="md:hidden relative px-8">
              <Carousel opts={{ align: 'start', loop: true }}>
                <CarouselContent className="-ml-4">
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Complete Oversight</h3>
                      <p className="text-sm text-slate-600">Keep your entire copyright portfolio organized and protected under expert management.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Proactive Monitoring</h3>
                      <p className="text-sm text-slate-600">Trademark Pact actively monitors your assets to detect unauthorized use early and maintain compliance.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Tailored Expertise</h3>
                      <p className="text-sm text-slate-600">Benefit from personalized strategies designed to fit your unique portfolio and business goals.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Maximize Asset Value</h3>
                      <p className="text-sm text-slate-600">Enhance the commercial potential of your copyrights through strategic licensing and protection measures.</p>
                    </div>
                  </CarouselItem>
                </CarouselContent>
                <CarouselPrevious className="border-emerald-200 text-[#064E3B]" />
                <CarouselNext className="border-emerald-200 text-[#064E3B]" />
              </Carousel>
            </div>
          </div>
        </section>
      ) : slug === 'dmca-compliance' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-benefits">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-4">
                Why Choose Trademark Pact for DMCA Compliance
              </h2>
            </div>
            <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-0">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Reliable Protection</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Safeguard your digital works with precise and legally compliant DMCA enforcement actions.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-1">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">End-to-End Support</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Trademark Pact manages all aspects of the DMCA process, from issuing notices to handling disputes, ensuring a seamless experience.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-2">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Specialized Expertise</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Work with legal professionals skilled in digital copyright law who tailor strategies to your specific needs.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-3">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Risk Reduction</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Prevent prolonged infringement and legal complications by enforcing your rights swiftly and effectively.</p>
              </div>
            </div>
            <div className="md:hidden relative px-8">
              <Carousel opts={{ align: 'start', loop: true }}>
                <CarouselContent className="-ml-4">
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Reliable Protection</h3>
                      <p className="text-sm text-slate-600">Safeguard your digital works with precise and legally compliant DMCA enforcement actions.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">End-to-End Support</h3>
                      <p className="text-sm text-slate-600">Trademark Pact manages all aspects of the DMCA process, from issuing notices to handling disputes, ensuring a seamless experience.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Specialized Expertise</h3>
                      <p className="text-sm text-slate-600">Work with legal professionals skilled in digital copyright law who tailor strategies to your specific needs.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Risk Reduction</h3>
                      <p className="text-sm text-slate-600">Prevent prolonged infringement and legal complications by enforcing your rights swiftly and effectively.</p>
                    </div>
                  </CarouselItem>
                </CarouselContent>
                <CarouselPrevious className="border-emerald-200 text-[#064E3B]" />
                <CarouselNext className="border-emerald-200 text-[#064E3B]" />
              </Carousel>
            </div>
          </div>
        </section>
      ) : slug === 'business-immigration-law' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-benefits">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-4">
                Why Choose Trademark Pact for Business Immigration Law
              </h2>
            </div>
            <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-0">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Comprehensive Expertise</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Navigate complex immigration requirements with experienced legal professionals.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-1">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">End-to-End Service</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Trademark Pact manages your entire immigration process, from application to compliance and dispute resolution.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-2">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Customized Solutions</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Receive tailored advice that aligns with your company's business goals and immigration needs.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-3">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Risk Mitigation</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Minimize legal risks by ensuring full compliance with immigration laws and regulations.</p>
              </div>
            </div>
            <div className="md:hidden relative px-8">
              <Carousel opts={{ align: 'start', loop: true }}>
                <CarouselContent className="-ml-4">
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Comprehensive Expertise</h3>
                      <p className="text-sm text-slate-600">Navigate complex immigration requirements with experienced legal professionals.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">End-to-End Service</h3>
                      <p className="text-sm text-slate-600">Trademark Pact manages your entire immigration process, from application to compliance and dispute resolution.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Customized Solutions</h3>
                      <p className="text-sm text-slate-600">Receive tailored advice that aligns with your company's business goals and immigration needs.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Risk Mitigation</h3>
                      <p className="text-sm text-slate-600">Minimize legal risks by ensuring full compliance with immigration laws and regulations.</p>
                    </div>
                  </CarouselItem>
                </CarouselContent>
                <CarouselPrevious className="border-emerald-200 text-[#064E3B]" />
                <CarouselNext className="border-emerald-200 text-[#064E3B]" />
              </Carousel>
            </div>
          </div>
        </section>
      ) : slug === 'due-diligence-process' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-benefits">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-4">
                Why Choose Trademark Pact for Due Diligence
              </h2>
            </div>
            <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-0">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Expert Evaluation</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Access thorough and reliable assessments conducted by experienced professionals.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-1">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">End-to-End Support</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Trademark Pact manages every stage of the due diligence process, ensuring nothing is overlooked.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-2">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Customized Approach</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Tailor investigations to the specific needs and context of your business.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-3">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Risk Mitigation</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Prevent costly mistakes by uncovering potential issues before finalizing deals.</p>
              </div>
            </div>
            <div className="md:hidden relative px-8">
              <Carousel opts={{ align: 'start', loop: true }}>
                <CarouselContent className="-ml-4">
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Expert Evaluation</h3>
                      <p className="text-sm text-slate-600">Access thorough and reliable assessments conducted by experienced professionals.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">End-to-End Support</h3>
                      <p className="text-sm text-slate-600">Trademark Pact manages every stage of the due diligence process, ensuring nothing is overlooked.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Customized Approach</h3>
                      <p className="text-sm text-slate-600">Tailor investigations to the specific needs and context of your business.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Risk Mitigation</h3>
                      <p className="text-sm text-slate-600">Prevent costly mistakes by uncovering potential issues before finalizing deals.</p>
                    </div>
                  </CarouselItem>
                </CarouselContent>
                <CarouselPrevious className="border-emerald-200 text-[#064E3B]" />
                <CarouselNext className="border-emerald-200 text-[#064E3B]" />
              </Carousel>
            </div>
          </div>
        </section>
      ) : slug === 'it-commercial-law' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-benefits">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-4">
                Why Choose Trademark Pact for IT Commercial Law
              </h2>
            </div>
            <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-0">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Industry-Specific Expertise</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Receive legal support from professionals familiar with technology and digital commerce challenges.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-1">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Comprehensive Coverage</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Trademark Pact manages all legal aspects related to IT contracts, compliance, and risk management.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-2">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Customized Solutions</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Benefit from contracts and strategies designed to fit your unique business model and technology needs.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-3">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Risk Reduction</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Minimize legal exposure by ensuring your IT operations comply with applicable laws and standards.</p>
              </div>
            </div>
            <div className="md:hidden relative px-8">
              <Carousel opts={{ align: 'start', loop: true }}>
                <CarouselContent className="-ml-4">
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Industry-Specific Expertise</h3>
                      <p className="text-sm text-slate-600">Receive legal support from professionals familiar with technology and digital commerce challenges.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Comprehensive Coverage</h3>
                      <p className="text-sm text-slate-600">Trademark Pact manages all legal aspects related to IT contracts, compliance, and risk management.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Customized Solutions</h3>
                      <p className="text-sm text-slate-600">Benefit from contracts and strategies designed to fit your unique business model and technology needs.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Risk Reduction</h3>
                      <p className="text-sm text-slate-600">Minimize legal exposure by ensuring your IT operations comply with applicable laws and standards.</p>
                    </div>
                  </CarouselItem>
                </CarouselContent>
                <CarouselPrevious className="border-emerald-200 text-[#064E3B]" />
                <CarouselNext className="border-emerald-200 text-[#064E3B]" />
              </Carousel>
            </div>
          </div>
        </section>
      ) : slug === 'partnerships-llps' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-benefits">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-4">
                Why Choose Trademark Pact for Partnerships and LLPs
              </h2>
            </div>
            <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-0">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Clear Legal Frameworks</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Ensure your partnership or LLP is founded on solid, enforceable agreements that protect all parties.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-1">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Full-Service Management</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Trademark Pact handles everything from initial formation to ongoing legal support and compliance checks.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-2">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Experienced Counsel</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Work with legal experts who understand the complexities of partnership and LLP law.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-3">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Risk Mitigation</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Prevent conflicts and legal issues with well-drafted agreements and proactive legal advice.</p>
              </div>
            </div>
            <div className="md:hidden relative px-8">
              <Carousel opts={{ align: 'start', loop: true }}>
                <CarouselContent className="-ml-4">
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Clear Legal Frameworks</h3>
                      <p className="text-sm text-slate-600">Ensure your partnership or LLP is founded on solid, enforceable agreements that protect all parties.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Full-Service Management</h3>
                      <p className="text-sm text-slate-600">Trademark Pact handles everything from initial formation to ongoing legal support and compliance checks.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Experienced Counsel</h3>
                      <p className="text-sm text-slate-600">Work with legal experts who understand the complexities of partnership and LLP law.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Risk Mitigation</h3>
                      <p className="text-sm text-slate-600">Prevent conflicts and legal issues with well-drafted agreements and proactive legal advice.</p>
                    </div>
                  </CarouselItem>
                </CarouselContent>
                <CarouselPrevious className="border-emerald-200 text-[#064E3B]" />
                <CarouselNext className="border-emerald-200 text-[#064E3B]" />
              </Carousel>
            </div>
          </div>
        </section>
      ) : slug === 'data-protection-privacy-law' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-benefits">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-4">
                Why Choose Trademark Pact for Data Protection and Privacy Law
              </h2>
            </div>
            <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-0">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Regulatory Expertise</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Stay ahead with guidance from professionals well-versed in global and local privacy laws.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-1">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">End-to-End Compliance</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Trademark Pact manages all aspects from policy creation to audits and incident management.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-2">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Tailored Solutions</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Receive customized advice designed to fit your industry, size, and data practices.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-3">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Risk Reduction</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Minimize legal exposure and reputational damage through proactive privacy management.</p>
              </div>
            </div>
            <div className="md:hidden relative px-8">
              <Carousel opts={{ align: 'start', loop: true }}>
                <CarouselContent className="-ml-4">
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Regulatory Expertise</h3>
                      <p className="text-sm text-slate-600">Stay ahead with guidance from professionals well-versed in global and local privacy laws.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">End-to-End Compliance</h3>
                      <p className="text-sm text-slate-600">Trademark Pact manages all aspects from policy creation to audits and incident management.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Tailored Solutions</h3>
                      <p className="text-sm text-slate-600">Receive customized advice designed to fit your industry, size, and data practices.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Risk Reduction</h3>
                      <p className="text-sm text-slate-600">Minimize legal exposure and reputational damage through proactive privacy management.</p>
                    </div>
                  </CarouselItem>
                </CarouselContent>
                <CarouselPrevious className="border-emerald-200 text-[#064E3B]" />
                <CarouselNext className="border-emerald-200 text-[#064E3B]" />
              </Carousel>
            </div>
          </div>
        </section>
      ) : slug === 'limited-liability-company' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-benefits">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-4">
                Why Choose Trademark Pact for LLC Services
              </h2>
            </div>
            <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-0">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Clear Legal Documentation</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Ensure your LLC is governed by well-crafted agreements that protect all members.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-1">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Full-Service Support</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Trademark Pact manages every aspect of your LLC's formation and ongoing legal needs.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-2">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Experienced Counsel</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Work with legal professionals who understand the nuances of LLC law and business operations.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-3">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Risk Prevention</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Avoid costly mistakes by ensuring full compliance and proactive legal management.</p>
              </div>
            </div>
            <div className="md:hidden relative px-8">
              <Carousel opts={{ align: 'start', loop: true }}>
                <CarouselContent className="-ml-4">
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Clear Legal Documentation</h3>
                      <p className="text-sm text-slate-600">Ensure your LLC is governed by well-crafted agreements that protect all members.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Full-Service Support</h3>
                      <p className="text-sm text-slate-600">Trademark Pact manages every aspect of your LLC's formation and ongoing legal needs.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Experienced Counsel</h3>
                      <p className="text-sm text-slate-600">Work with legal professionals who understand the nuances of LLC law and business operations.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Risk Prevention</h3>
                      <p className="text-sm text-slate-600">Avoid costly mistakes by ensuring full compliance and proactive legal management.</p>
                    </div>
                  </CarouselItem>
                </CarouselContent>
                <CarouselPrevious className="border-emerald-200 text-[#064E3B]" />
                <CarouselNext className="border-emerald-200 text-[#064E3B]" />
              </Carousel>
            </div>
          </div>
        </section>
      ) : slug === 'c-corporation' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-benefits">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-4">
                Why Choose Trademark Pact for C Corporation Services
              </h2>
            </div>
            <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-0">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Clear Corporate Governance</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Ensure your C Corporation is structured with thorough bylaws protecting shareholders and executives.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-1">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Complete Incorporation Assistance</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Trademark Pact manages every step from document preparation to regulatory compliance.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-2">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Experienced Legal Counsel</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Work with professionals knowledgeable in corporate law and business regulations.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-3">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Risk Management</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Minimize legal and financial risks through proper compliance and proactive legal support.</p>
              </div>
            </div>
            <div className="md:hidden relative px-8">
              <Carousel opts={{ align: 'start', loop: true }}>
                <CarouselContent className="-ml-4">
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Clear Corporate Governance</h3>
                      <p className="text-sm text-slate-600">Ensure your C Corporation is structured with thorough bylaws protecting shareholders and executives.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Complete Incorporation Assistance</h3>
                      <p className="text-sm text-slate-600">Trademark Pact manages every step from document preparation to regulatory compliance.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Experienced Legal Counsel</h3>
                      <p className="text-sm text-slate-600">Work with professionals knowledgeable in corporate law and business regulations.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Risk Management</h3>
                      <p className="text-sm text-slate-600">Minimize legal and financial risks through proper compliance and proactive legal support.</p>
                    </div>
                  </CarouselItem>
                </CarouselContent>
                <CarouselPrevious className="border-emerald-200 text-[#064E3B]" />
                <CarouselNext className="border-emerald-200 text-[#064E3B]" />
              </Carousel>
            </div>
          </div>
        </section>
      ) : slug === 's-corporation' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-benefits">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-4">
                Why Choose Trademark Pact for S Corporation Services
              </h2>
            </div>
            <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-0">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Tax Efficiency</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Help you leverage the unique tax advantages of an S Corporation structure.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-1">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Complete Support</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Trademark Pact handles everything from election filing to ongoing compliance and corporate governance.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-2">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Experienced Legal Advisors</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Work with professionals specializing in corporate and tax law tailored to your business needs.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-3">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Risk Minimization</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Ensure your S Corporation meets all legal obligations to avoid penalties and protect shareholder interests.</p>
              </div>
            </div>
            <div className="md:hidden relative px-8">
              <Carousel opts={{ align: 'start', loop: true }}>
                <CarouselContent className="-ml-4">
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Tax Efficiency</h3>
                      <p className="text-sm text-slate-600">Help you leverage the unique tax advantages of an S Corporation structure.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Complete Support</h3>
                      <p className="text-sm text-slate-600">Trademark Pact handles everything from election filing to ongoing compliance and corporate governance.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Experienced Legal Advisors</h3>
                      <p className="text-sm text-slate-600">Work with professionals specializing in corporate and tax law tailored to your business needs.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Risk Minimization</h3>
                      <p className="text-sm text-slate-600">Ensure your S Corporation meets all legal obligations to avoid penalties and protect shareholder interests.</p>
                    </div>
                  </CarouselItem>
                </CarouselContent>
                <CarouselPrevious className="border-emerald-200 text-[#064E3B]" />
                <CarouselNext className="border-emerald-200 text-[#064E3B]" />
              </Carousel>
            </div>
          </div>
        </section>
      ) : slug === 'non-profit' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-benefits">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-4">
                Why Choose Trademark Pact for Non-Profit Services
              </h2>
            </div>
            <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-0">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Clear Organizational Structure</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Ensure your non-profit is founded on strong governance documents that support transparency and accountability.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-1">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Complete Formation Support</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Trademark Pact manages everything from initial filings to ongoing compliance and regulatory updates.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-2">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Expert Legal Guidance</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Work with attorneys experienced in non-profit law who understand the unique challenges of the sector.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-3">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Risk Management</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Avoid common legal pitfalls and maintain compliance to protect your mission and reputation.</p>
              </div>
            </div>
            <div className="md:hidden relative px-8">
              <Carousel opts={{ align: 'start', loop: true }}>
                <CarouselContent className="-ml-4">
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Clear Organizational Structure</h3>
                      <p className="text-sm text-slate-600">Ensure your non-profit is founded on strong governance documents that support transparency and accountability.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Complete Formation Support</h3>
                      <p className="text-sm text-slate-600">Trademark Pact manages everything from initial filings to ongoing compliance and regulatory updates.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Expert Legal Guidance</h3>
                      <p className="text-sm text-slate-600">Work with attorneys experienced in non-profit law who understand the unique challenges of the sector.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Risk Management</h3>
                      <p className="text-sm text-slate-600">Avoid common legal pitfalls and maintain compliance to protect your mission and reputation.</p>
                    </div>
                  </CarouselItem>
                </CarouselContent>
                <CarouselPrevious className="border-emerald-200 text-[#064E3B]" />
                <CarouselNext className="border-emerald-200 text-[#064E3B]" />
              </Carousel>
            </div>
          </div>
        </section>
      ) : slug === 'search-existing-patent' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-benefits">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-4">
                Why Choose Trademark Pact for Patent Searches
              </h2>
            </div>
            <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-0">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Thorough Investigation</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Access exhaustive patent databases to ensure no relevant patents are overlooked.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-1">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">End-to-End Support</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Trademark Pact manages the entire search process and helps interpret findings to guide your innovation.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-2">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Specialized Expertise</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Work with patent professionals skilled in diverse technical fields and patent law.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-3">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Risk Mitigation</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Avoid costly patent infringements and strengthen your application by understanding the existing patent landscape.</p>
              </div>
            </div>
            <div className="md:hidden relative px-8">
              <Carousel opts={{ align: 'start', loop: true }}>
                <CarouselContent className="-ml-4">
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Thorough Investigation</h3>
                      <p className="text-sm text-slate-600">Access exhaustive patent databases to ensure no relevant patents are overlooked.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">End-to-End Support</h3>
                      <p className="text-sm text-slate-600">Trademark Pact manages the entire search process and helps interpret findings to guide your innovation.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Specialized Expertise</h3>
                      <p className="text-sm text-slate-600">Work with patent professionals skilled in diverse technical fields and patent law.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Risk Mitigation</h3>
                      <p className="text-sm text-slate-600">Avoid costly patent infringements and strengthen your application by understanding the existing patent landscape.</p>
                    </div>
                  </CarouselItem>
                </CarouselContent>
                <CarouselPrevious className="border-emerald-200 text-[#064E3B]" />
                <CarouselNext className="border-emerald-200 text-[#064E3B]" />
              </Carousel>
            </div>
          </div>
        </section>
      ) : slug === 'patent-office-actions-support' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-benefits">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-4">
                Why Choose Trademark Pact for Patent Office Actions
              </h2>
            </div>
            <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-0">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Expert Guidance</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Benefit from experienced patent professionals who understand the nuances of patent prosecution.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-1">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Full-Service Support</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Trademark Pact manages the entire response process, reducing the risk of delays or errors.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-2">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Tailored Strategies</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Receive customized advice to overcome specific examiner concerns and strengthen your application.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-3">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Maximize Patent Success</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Improve your chances of patent allowance with thorough, well-crafted responses.</p>
              </div>
            </div>
            <div className="md:hidden relative px-8">
              <Carousel opts={{ align: 'start', loop: true }}>
                <CarouselContent className="-ml-4">
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Expert Guidance</h3>
                      <p className="text-sm text-slate-600">Benefit from experienced patent professionals who understand the nuances of patent prosecution.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Full-Service Support</h3>
                      <p className="text-sm text-slate-600">Trademark Pact manages the entire response process, reducing the risk of delays or errors.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Tailored Strategies</h3>
                      <p className="text-sm text-slate-600">Receive customized advice to overcome specific examiner concerns and strengthen your application.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Maximize Patent Success</h3>
                      <p className="text-sm text-slate-600">Improve your chances of patent allowance with thorough, well-crafted responses.</p>
                    </div>
                  </CarouselItem>
                </CarouselContent>
                <CarouselPrevious className="border-emerald-200 text-[#064E3B]" />
                <CarouselNext className="border-emerald-200 text-[#064E3B]" />
              </Carousel>
            </div>
          </div>
        </section>
      ) : slug === 'patent-application-submission' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-benefits">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-4">
                Why Choose Trademark Pact for Patent Application Submission
              </h2>
            </div>
            <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-0">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Expert Preparation</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Work with skilled professionals who understand patent law and technical documentation.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-1">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">End-to-End Management</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Trademark Pact handles every stage, from drafting to filing, ensuring a seamless experience.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-2">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Customized Solutions</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Receive personalized attention tailored to the unique aspects of your invention and business goals.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-3">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Maximize Protection</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Increase your chances of patent approval through accurate and thorough application submissions.</p>
              </div>
            </div>
            <div className="md:hidden relative px-8">
              <Carousel opts={{ align: 'start', loop: true }}>
                <CarouselContent className="-ml-4">
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Expert Preparation</h3>
                      <p className="text-sm text-slate-600">Work with skilled professionals who understand patent law and technical documentation.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">End-to-End Management</h3>
                      <p className="text-sm text-slate-600">Trademark Pact handles every stage, from drafting to filing, ensuring a seamless experience.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Customized Solutions</h3>
                      <p className="text-sm text-slate-600">Receive personalized attention tailored to the unique aspects of your invention and business goals.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Maximize Protection</h3>
                      <p className="text-sm text-slate-600">Increase your chances of patent approval through accurate and thorough application submissions.</p>
                    </div>
                  </CarouselItem>
                </CarouselContent>
                <CarouselPrevious className="border-emerald-200 text-[#064E3B]" />
                <CarouselNext className="border-emerald-200 text-[#064E3B]" />
              </Carousel>
            </div>
          </div>
        </section>
      ) : slug === 'legal-representation-patent' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-benefits">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-4">
                Why Choose Trademark Pact for Patent Legal Representation
              </h2>
            </div>
            <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-0">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Strong Legal Advocacy</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Protect your patents with experienced attorneys skilled in intellectual property law.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-1">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Comprehensive Support</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Trademark Pact handles all aspects of patent-related legal matters, from enforcement to licensing and litigation.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-2">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Tailored Strategies</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Receive personalized legal plans designed to fit your specific patent portfolio and business objectives.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-3">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Risk Reduction</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Minimize potential losses and strengthen your patent rights through proactive legal representation.</p>
              </div>
            </div>
            <div className="md:hidden relative px-8">
              <Carousel opts={{ align: 'start', loop: true }}>
                <CarouselContent className="-ml-4">
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Strong Legal Advocacy</h3>
                      <p className="text-sm text-slate-600">Protect your patents with experienced attorneys skilled in intellectual property law.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Comprehensive Support</h3>
                      <p className="text-sm text-slate-600">Trademark Pact handles all aspects of patent-related legal matters, from enforcement to licensing and litigation.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Tailored Strategies</h3>
                      <p className="text-sm text-slate-600">Receive personalized legal plans designed to fit your specific patent portfolio and business objectives.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Risk Reduction</h3>
                      <p className="text-sm text-slate-600">Minimize potential losses and strengthen your patent rights through proactive legal representation.</p>
                    </div>
                  </CarouselItem>
                </CarouselContent>
                <CarouselPrevious className="border-emerald-200 text-[#064E3B]" />
                <CarouselNext className="border-emerald-200 text-[#064E3B]" />
              </Carousel>
            </div>
          </div>
        </section>
      ) : slug === 'amazon-brand-registry' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-benefits">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-4">
                Why Choose Trademark Pact for Amazon Brand Registry
              </h2>
            </div>
            <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-0">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Streamlined Enrollment</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Navigate Amazon's registration process smoothly with professional assistance.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-1">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Comprehensive Protection</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Trademark Pact helps safeguard your brand from infringement and counterfeit listings.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-2">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Expert Knowledge</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Benefit from our deep understanding of Amazon's policies and trademark law.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-3">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Maximize Brand Value</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Use Amazon's advanced tools effectively to boost your brand's presence and customer trust.</p>
              </div>
            </div>
            <div className="md:hidden relative px-8">
              <Carousel opts={{ align: 'start', loop: true }}>
                <CarouselContent className="-ml-4">
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Streamlined Enrollment</h3>
                      <p className="text-sm text-slate-600">Navigate Amazon's registration process smoothly with professional assistance.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Comprehensive Protection</h3>
                      <p className="text-sm text-slate-600">Trademark Pact helps safeguard your brand from infringement and counterfeit listings.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Expert Knowledge</h3>
                      <p className="text-sm text-slate-600">Benefit from our deep understanding of Amazon's policies and trademark law.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Maximize Brand Value</h3>
                      <p className="text-sm text-slate-600">Use Amazon's advanced tools effectively to boost your brand's presence and customer trust.</p>
                    </div>
                  </CarouselItem>
                </CarouselContent>
                <CarouselPrevious className="border-emerald-200 text-[#064E3B]" />
                <CarouselNext className="border-emerald-200 text-[#064E3B]" />
              </Carousel>
            </div>
          </div>
        </section>
      ) : slug === 'bookkeeping-service' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-benefits">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-4">
                Why Choose Trademark Pact for Bookkeeping
              </h2>
            </div>
            <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-0">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Accurate Records</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Maintain precise and error-free financial data critical for business decisions and compliance.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-1">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Comprehensive Management</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Trademark Pact manages all bookkeeping tasks, freeing you to focus on growing your business.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-2">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Expert Oversight</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Benefit from professional review and advice to optimize your financial processes.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-3">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Improved Financial Control</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Gain better visibility and control over your finances through organized bookkeeping.</p>
              </div>
            </div>
            <div className="md:hidden relative px-8">
              <Carousel opts={{ align: 'start', loop: true }}>
                <CarouselContent className="-ml-4">
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Accurate Records</h3>
                      <p className="text-sm text-slate-600">Maintain precise and error-free financial data critical for business decisions and compliance.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Comprehensive Management</h3>
                      <p className="text-sm text-slate-600">Trademark Pact manages all bookkeeping tasks, freeing you to focus on growing your business.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Expert Oversight</h3>
                      <p className="text-sm text-slate-600">Benefit from professional review and advice to optimize your financial processes.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Improved Financial Control</h3>
                      <p className="text-sm text-slate-600">Gain better visibility and control over your finances through organized bookkeeping.</p>
                    </div>
                  </CarouselItem>
                </CarouselContent>
                <CarouselPrevious className="border-emerald-200 text-[#064E3B]" />
                <CarouselNext className="border-emerald-200 text-[#064E3B]" />
              </Carousel>
            </div>
          </div>
        </section>
      ) : slug === 'business-formation-services' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-benefits">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-4">
                Why Choose Trademark Pact for Business Formation
              </h2>
            </div>
            <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-0">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Tailored Solutions</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Receive personalized advice to select the ideal business structure for your unique needs.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-1">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Complete Service</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Trademark Pact manages all formation paperwork and compliance tasks from start to finish.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-2">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Experienced Professionals</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Work with legal experts who understand business law and regulatory requirements.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-3">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Risk Prevention</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Avoid costly mistakes by ensuring your business is properly set up and compliant from day one.</p>
              </div>
            </div>
            <div className="md:hidden relative px-8">
              <Carousel opts={{ align: 'start', loop: true }}>
                <CarouselContent className="-ml-4">
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Tailored Solutions</h3>
                      <p className="text-sm text-slate-600">Receive personalized advice to select the ideal business structure for your unique needs.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Complete Service</h3>
                      <p className="text-sm text-slate-600">Trademark Pact manages all formation paperwork and compliance tasks from start to finish.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Experienced Professionals</h3>
                      <p className="text-sm text-slate-600">Work with legal experts who understand business law and regulatory requirements.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Risk Prevention</h3>
                      <p className="text-sm text-slate-600">Avoid costly mistakes by ensuring your business is properly set up and compliant from day one.</p>
                    </div>
                  </CarouselItem>
                </CarouselContent>
                <CarouselPrevious className="border-emerald-200 text-[#064E3B]" />
                <CarouselNext className="border-emerald-200 text-[#064E3B]" />
              </Carousel>
            </div>
          </div>
        </section>
      ) : slug === 'certificate-good-standing' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-benefits">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-4">
                Why Choose Trademark Pact for Your Certificate of Good Standing
              </h2>
            </div>
            <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-0">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Accurate Compliance Checks</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Ensure your business fulfills all obligations before requesting the certificate to avoid delays.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-1">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Full-Service Management</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Trademark Pact handles the entire process from verification to certificate delivery, saving you time.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-2">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Experienced Guidance</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Receive expert advice on maintaining your business's good standing for ongoing compliance.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-3">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Efficient and Reliable</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Benefit from a streamlined service designed to obtain your certificate quickly and hassle-free.</p>
              </div>
            </div>
            <div className="md:hidden relative px-8">
              <Carousel opts={{ align: 'start', loop: true }}>
                <CarouselContent className="-ml-4">
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Accurate Compliance Checks</h3>
                      <p className="text-sm text-slate-600">Ensure your business fulfills all obligations before requesting the certificate to avoid delays.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Full-Service Management</h3>
                      <p className="text-sm text-slate-600">Trademark Pact handles the entire process from verification to certificate delivery, saving you time.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Experienced Guidance</h3>
                      <p className="text-sm text-slate-600">Receive expert advice on maintaining your business's good standing for ongoing compliance.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Efficient and Reliable</h3>
                      <p className="text-sm text-slate-600">Benefit from a streamlined service designed to obtain your certificate quickly and hassle-free.</p>
                    </div>
                  </CarouselItem>
                </CarouselContent>
                <CarouselPrevious className="border-emerald-200 text-[#064E3B]" />
                <CarouselNext className="border-emerald-200 text-[#064E3B]" />
              </Carousel>
            </div>
          </div>
        </section>
      ) : slug === 'company-dissolution' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-benefits">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-4">
                Why Choose Trademark Pact for Company Dissolution
              </h2>
            </div>
            <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-0">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Legal Compliance</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Ensure your dissolution meets all state and federal legal requirements to avoid future complications.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-1">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Complete Process Management</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Trademark Pact handles all filings, notifications, and compliance tasks from start to finish.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-2">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Expert Guidance</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Receive professional advice on tax filings, asset distribution, and creditor settlements.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-3">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Liability Protection</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Properly close your business to minimize personal liability and protect your financial interests.</p>
              </div>
            </div>
            <div className="md:hidden relative px-8">
              <Carousel opts={{ align: 'start', loop: true }}>
                <CarouselContent className="-ml-4">
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Legal Compliance</h3>
                      <p className="text-sm text-slate-600">Ensure your dissolution meets all state and federal legal requirements to avoid future complications.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Complete Process Management</h3>
                      <p className="text-sm text-slate-600">Trademark Pact handles all filings, notifications, and compliance tasks from start to finish.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Expert Guidance</h3>
                      <p className="text-sm text-slate-600">Receive professional advice on tax filings, asset distribution, and creditor settlements.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Liability Protection</h3>
                      <p className="text-sm text-slate-600">Properly close your business to minimize personal liability and protect your financial interests.</p>
                    </div>
                  </CarouselItem>
                </CarouselContent>
                <CarouselPrevious className="border-emerald-200 text-[#064E3B]" />
                <CarouselNext className="border-emerald-200 text-[#064E3B]" />
              </Carousel>
            </div>
          </div>
        </section>
      ) : slug === 'company-name-change' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-benefits">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-4">
                Why Choose Trademark Pact for Company Name Change
              </h2>
            </div>
            <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-0">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Name Availability Verification</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Confirm your new name is available and meets all state naming requirements before filing.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-1">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Full-Service Processing</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Trademark Pact manages all document preparation, filings, and updates for a seamless transition.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-2">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Comprehensive Updates</h3>
                <p className="text-sm text-slate-600 leading-relaxed">We update your business name across tax registrations, licenses, and other official records.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid="service-benefit-3">
                <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Expert Legal Support</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Receive professional guidance to ensure your name change is legally compliant and properly executed.</p>
              </div>
            </div>
            <div className="md:hidden relative px-8">
              <Carousel opts={{ align: 'start', loop: true }}>
                <CarouselContent className="-ml-4">
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Name Availability Verification</h3>
                      <p className="text-sm text-slate-600">Confirm your new name is available and meets all state naming requirements before filing.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Full-Service Processing</h3>
                      <p className="text-sm text-slate-600">Trademark Pact manages all document preparation, filings, and updates for a seamless transition.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Comprehensive Updates</h3>
                      <p className="text-sm text-slate-600">We update your business name across tax registrations, licenses, and other official records.</p>
                    </div>
                  </CarouselItem>
                  <CarouselItem className="pl-4 basis-[100%]">
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                      <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">Expert Legal Support</h3>
                      <p className="text-sm text-slate-600">Receive professional guidance to ensure your name change is legally compliant and properly executed.</p>
                    </div>
                  </CarouselItem>
                </CarouselContent>
                <CarouselPrevious className="border-emerald-200 text-[#064E3B]" />
                <CarouselNext className="border-emerald-200 text-[#064E3B]" />
              </Carousel>
            </div>
          </div>
        </section>
      ) : service.benefits && service.benefits.length > 0 ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-benefits">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-4">
                Key Benefits
              </h2>
            </div>
            <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-6">
              {service.benefits.map((b, i) => (
                <div key={i} className="bg-white p-6 rounded-2xl border border-emerald-100 card-hover" data-testid={`service-benefit-${i}`}>
                  <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                  <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">{b.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
            <div className="md:hidden relative px-8">
              <Carousel opts={{ align: 'start', loop: true }}>
                <CarouselContent className="-ml-4">
                  {service.benefits.map((b, i) => (
                    <CarouselItem key={i} className="pl-4 basis-[100%]">
                      <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                        <CheckCircle className="w-8 h-8 text-[#10B981] mb-4" />
                        <h3 className="font-heading font-bold text-sm text-[#064E3B] mb-2">{b.title}</h3>
                        <p className="text-sm text-slate-600">{b.desc}</p>
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <CarouselPrevious className="border-emerald-200 text-[#064E3B]" />
                <CarouselNext className="border-emerald-200 text-[#064E3B]" />
              </Carousel>
            </div>
          </div>
        </section>
      ) : null}

      {/* 4. Pricing (only for trademark registration) */}
      {service.hasPricing && <PricingSection />}

      {/* 6. FAQs */}
      {slug === 'active-monitoring-trademark-infringement' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-faqs">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-8">
                  Frequently Asked Questions
                </h2>
                <Accordion type="single" collapsible className="space-y-3">
                  <AccordionItem value="faq-0" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-0">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      What is Trademark Pact's active monitoring service?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Trademark Pact's active monitoring service continuously scans trademark databases, online platforms, and marketplace listings to detect unauthorized use and conflicting filings, ensuring your trademark rights remain fully protected at all times.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-1" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-1">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      Why is active trademark monitoring important after registration?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Ongoing monitoring helps identify potential infringements and conflicts early, reducing the risk of brand dilution, loss of trademark rights, and costly legal battles after unauthorized use has already spread.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-2" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-2">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      How thorough is Trademark Pact's monitoring coverage?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Our monitoring covers all relevant channels including federal trademark databases, e-commerce marketplaces, social media platforms, and domain registrations, providing a complete picture of every potential threat to your mark.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-3" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-3">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      What happens after a potential infringement is detected?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Trademark Pact provides you with a detailed alert and expert analysis of the detected threat along with clear recommendations on the most appropriate and effective course of action to protect your trademark.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-4" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-4">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      How does Trademark Pact help save costs?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      By detecting unauthorized use and conflicting filings early, Trademark Pact helps you avoid expensive trademark litigation, brand rehabilitation efforts, and the significant cost of rebuilding damaged brand recognition.
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&q=80&auto=format"
                  alt="FAQ support"
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 'clearance-search-services' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-faqs">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-8">
                  Frequently Asked Questions
                </h2>
                <Accordion type="single" collapsible className="space-y-3">
                  <AccordionItem value="faq-0" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-0">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      What is Trademark Pact's clearance search service?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Trademark Pact's clearance search service thoroughly checks existing trademarks at federal, state, and common law levels to ensure your proposed brand is available for use and registration.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-1" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-1">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      Why is a trademark clearance search important before branding?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      A clearance search helps identify potential conflicts early, reducing the risk of costly legal issues or rebranding after you've invested in your brand.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-2" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-2">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      How comprehensive is Trademark Pact's search?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Our search covers all relevant databases, including federal, state, and common law trademarks, providing a full picture of possible conflicts and registrability.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-3" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-3">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      What happens after the search is completed?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Trademark Pact provides you with a detailed analysis of the search results along with expert recommendations to guide your next steps.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-4" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-4">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      How does Trademark Pact help save costs?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      By identifying conflicts early, Trademark Pact helps you avoid expensive rebranding and legal disputes, ensuring a smoother and more cost-effective branding process.
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&q=80&auto=format"
                  alt="FAQ support"
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 'file-ttab-petition' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-faqs">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-8">
                  Frequently Asked Questions
                </h2>
                <Accordion type="single" collapsible className="space-y-3">
                  <AccordionItem value="faq-0" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-0">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      What is Trademark Pact's TTAB petition filing service?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Trademark Pact's TTAB petition filing service thoroughly reviews your trademark dispute and prepares complete, well-argued petitions to ensure your case is formally and effectively presented before the Trademark Trial and Appeal Board.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-1" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-1">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      Why is filing a TTAB petition important for trademark protection?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      A properly filed petition allows you to formally challenge conflicting registrations and oppose harmful trademarks early, reducing the risk of brand damage or permanent loss of trademark rights after unauthorized registrations become established.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-2" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-2">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      How thorough is Trademark Pact's petition preparation process?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Our process covers all relevant grounds including likelihood of confusion, abandonment claims, fraud on the USPTO, descriptiveness challenges, and procedural requirements, providing a complete and strategically sound petition every time.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-3" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-3">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      What happens after the TTAB petition is filed?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Trademark Pact provides you with confirmation of the completed filing along with expert guidance on expected board proceedings, key upcoming deadlines, and the anticipated timeline toward a favorable resolution of your trademark matter.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-4" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-4">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      How does Trademark Pact help save costs during TTAB proceedings?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      By ensuring every petition is properly prepared and every procedural requirement is accurately met, Trademark Pact helps you avoid costly filing errors, unnecessary delays, and the significant expense of correcting mistakes during active board proceedings.
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&q=80&auto=format"
                  alt="FAQ support"
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 'trademark-ownership-transfers' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-faqs">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-8">
                  Frequently Asked Questions
                </h2>
                <Accordion type="single" collapsible className="space-y-3">
                  <AccordionItem value="faq-0" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-0">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      What is Trademark Pact's ownership transfer service?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Trademark Pact's ownership transfer service manages the complete legal process of reassigning a trademark from one party to another, including drafting assignment agreements and recording the change with the USPTO.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-1" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-1">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      Why does trademark ownership need to be formally transferred?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      A formal transfer ensures the new owner has legally recognized rights to the mark. Without proper recordation, ownership disputes, licensing issues, or future registration complications can arise unexpectedly.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-2" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-2">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      What does Trademark Pact handle during the transfer process?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Trademark Pact manages all aspects of the transfer — from preparing and reviewing the assignment agreement to filing the necessary paperwork with the USPTO to officially record the new ownership.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-3" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-3">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      What happens after the trademark transfer is completed?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Once the transfer is recorded, Trademark Pact provides you with confirmation of the completed assignment along with guidance on maintaining and protecting your trademark under the new ownership.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-4" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-4">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      How does Trademark Pact help prevent costly mistakes during a transfer?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      By ensuring every document is properly drafted and every filing is accurately submitted, Trademark Pact eliminates common errors that can invalidate a transfer or expose either party to legal liability.
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&q=80&auto=format"
                  alt="FAQ support"
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 'submission-of-statements-of-use' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-faqs">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-8">
                  Frequently Asked Questions
                </h2>
                <Accordion type="single" collapsible className="space-y-3">
                  <AccordionItem value="faq-0" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-0">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      What is Trademark Pact's statement of use service?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Trademark Pact's statement of use service manages the complete process of confirming your trademark is actively used in commerce, including preparing the declaration, selecting the right specimen, and submitting everything to the USPTO on time.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-1" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-1">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      Why does a statement of use need to be formally submitted?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      A formal submission confirms to the USPTO that your trademark is genuinely in use in commerce. Without it, your application will be considered abandoned and you will lose the rights you worked to establish.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-2" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-2">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      What does Trademark Pact handle during the submission process?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Trademark Pact manages all aspects of the filing from preparing and reviewing the statement of use declaration to identifying the appropriate specimen and submitting the complete package to the USPTO accurately.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-3" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-3">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      What happens after the statement of use is submitted?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Once the submission is accepted, Trademark Pact provides you with confirmation of the completed filing along with guidance on the next steps toward securing and maintaining your full trademark registration.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-4" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-4">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      How does Trademark Pact help prevent costly mistakes during submission?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      By ensuring every declaration is properly prepared and every specimen meets USPTO standards, Trademark Pact eliminates common errors that can trigger office actions, cause delays, or result in outright application abandonment.
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&q=80&auto=format"
                  alt="FAQ support"
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 'support-response-trademark-office-actions' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-faqs">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-8">
                  Frequently Asked Questions
                </h2>
                <Accordion type="single" collapsible className="space-y-3">
                  <AccordionItem value="faq-0" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-0">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      What is Trademark Pact's office action response service?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Trademark Pact's office action response service thoroughly reviews USPTO refusals and prepares complete, well-argued responses to ensure your trademark application remains active and successfully advances toward full registration.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-1" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-1">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      Why is responding to a trademark office action important?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      A proper and timely response helps overcome examiner objections early, reducing the risk of application abandonment or final refusal after you have already invested time and money into your trademark filing.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-2" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-2">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      How thorough is Trademark Pact's response process?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Our process covers all relevant refusal grounds including likelihood of confusion, descriptiveness, specimen deficiencies, and procedural requirements, providing a complete and strategically sound response every time.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-3" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-3">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      What happens after the office action response is submitted?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Trademark Pact provides you with confirmation of the completed filing along with expert guidance on the expected next steps and anticipated timeline toward securing your full trademark registration.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-4" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-4">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      How does Trademark Pact help save costs?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      By addressing every USPTO concern accurately and on time, Trademark Pact helps you avoid repeated office actions, application abandonment, and the significant expense of starting the filing process over entirely.
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&q=80&auto=format"
                  alt="FAQ support"
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 'register-your-copyright' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-faqs">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-8">
                  Frequently Asked Questions
                </h2>
                <Accordion type="single" collapsible className="space-y-3">
                  <AccordionItem value="faq-0" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-0">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      What is Trademark Pact's copyright registration service?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Trademark Pact's service helps creators officially register their original works with the U.S. Copyright Office, covering everything from application preparation to filing and follow-up.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-1" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-1">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      Why is registering a copyright important?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Registration provides legal proof of ownership and enables stronger protections against infringement, helping you enforce your rights if someone copies or misuses your work.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-2" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-2">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      What types of works can Trademark Pact register?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      We assist with registering a wide range of creative works including written content, artwork, music, software, films, and digital media.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-3" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-3">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      How long does the copyright registration process take?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      While timelines vary, Trademark Pact keeps you updated throughout the process and helps expedite your application to secure your copyright as efficiently as possible.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-4" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-4">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      How does Trademark Pact ensure a smooth registration?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      We handle all documentation carefully, avoid common pitfalls, and liaise directly with the copyright office to ensure your application is accurate and complete.
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&q=80&auto=format"
                  alt="FAQ support"
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 'copyright-enforcement-assistance' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-faqs">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-8">
                  Frequently Asked Questions
                </h2>
                <Accordion type="single" collapsible className="space-y-3">
                  <AccordionItem value="faq-0" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-0">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      What does Trademark Pact's copyright enforcement service entail?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Trademark Pact assists copyright owners in protecting their works by handling infringement disputes, issuing legal warnings, and providing continued monitoring.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-1" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-1">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      Why is it critical to enforce copyright rights?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Enforcement deters illegal use, defends your legal claims, and helps prevent potential revenue losses due to infringement.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-2" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-2">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      What enforcement actions does Trademark Pact provide?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      We prepare cease-and-desist letters, negotiate resolutions, and support legal escalation when needed to stop infringers.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-3" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-3">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      How does Trademark Pact identify copyright violations?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      We employ specialized tracking tools and techniques to detect unauthorized use across digital and physical platforms, informing you promptly.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-4" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-4">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      How does Trademark Pact minimize errors in enforcement?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Our expert review and professional management of all communications reduce the risk of mistakes that could undermine your legal position or enforcement success.
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&q=80&auto=format"
                  alt="FAQ support"
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 'copyright-transfers-licensing' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-faqs">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-8">
                  Frequently Asked Questions
                </h2>
                <Accordion type="single" collapsible className="space-y-3">
                  <AccordionItem value="faq-0" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-0">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      What is Trademark Pact's copyright transfers and licensing service?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Trademark Pact facilitates the legal transfer of copyright ownership and the drafting of licensing agreements to authorize third-party use of your works.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-1" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-1">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      Why are formal agreements important for copyright transfers and licenses?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Proper agreements establish clear terms and protect your rights by preventing unauthorized use or ownership conflicts.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-2" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-2">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      What does Trademark Pact handle in the transfer or licensing process?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      We prepare, review, and finalize all contracts, ensure legal compliance, and offer strategic advice during negotiations.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-3" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-3">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      Can Trademark Pact support both exclusive and non-exclusive licenses?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Yes, we tailor licensing agreements to fit exclusive or non-exclusive arrangements based on your goals and preferences.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-4" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-4">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      How does Trademark Pact help prevent legal issues in transfers and licenses?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      By ensuring contracts are comprehensive and legally sound, Trademark Pact minimizes risks of disputes or enforcement challenges.
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&q=80&auto=format"
                  alt="FAQ support"
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 'legal-representation-copyright' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-faqs">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-8">
                  Frequently Asked Questions
                </h2>
                <Accordion type="single" collapsible className="space-y-3">
                  <AccordionItem value="faq-0" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-0">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      What does Trademark Pact's copyright legal representation service involve?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Trademark Pact offers professional legal advocacy for copyright owners facing infringement, including negotiations, legal filings, and court representation.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-1" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-1">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      When should I seek legal representation for copyright issues?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Legal representation is crucial when infringement escalates beyond informal resolution or when protecting your rights requires formal legal action.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-2" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-2">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      What types of legal actions can Trademark Pact assist with?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      We support cease-and-desist negotiations, settlement discussions, and full-scale litigation to enforce and defend your copyrights.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-3" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-3">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      How does Trademark Pact manage my case?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Our attorneys provide regular updates, strategic advice, and handle all filings and communications on your behalf.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-4" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-4">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      How does Trademark Pact help minimize legal risks?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      By providing expert legal counsel and thorough case management, we reduce the chance of errors or oversights that could weaken your copyright claims.
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&q=80&auto=format"
                  alt="FAQ support"
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 'copyright-portfolio-management' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-faqs">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-8">
                  Frequently Asked Questions
                </h2>
                <Accordion type="single" collapsible className="space-y-3">
                  <AccordionItem value="faq-0" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-0">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      What does Trademark Pact's copyright portfolio management service involve?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Trademark Pact manages your collection of copyrighted works by organizing records, monitoring rights, and offering strategic advice to protect and maximize their value.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-1" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-1">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      Why is managing a copyright portfolio important?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Proper portfolio management ensures you know the status of all assets, preventing infringement and enabling better licensing or monetization decisions.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-2" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-2">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      How does Trademark Pact monitor my copyright portfolio?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      We track registrations, licenses, and potential unauthorized uses across various channels to keep your rights secure.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-3" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-3">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      Can Trademark Pact help with licensing strategies?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Yes, we provide expert advice on licensing opportunities to increase the commercial value of your copyrights.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-4" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-4">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      How does portfolio management reduce legal risks?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      By maintaining accurate documentation and monitoring usage, Trademark Pact helps prevent disputes and protects your intellectual property rights effectively.
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&q=80&auto=format"
                  alt="FAQ support"
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 'dmca-compliance' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-faqs">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-8">
                  Frequently Asked Questions
                </h2>
                <Accordion type="single" collapsible className="space-y-3">
                  <AccordionItem value="faq-0" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-0">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      What does Trademark Pact's DMCA compliance service involve?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Trademark Pact assists copyright holders in enforcing their rights under the DMCA through takedown notices, counter-notice responses, and ongoing monitoring.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-1" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-1">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      Why is DMCA compliance important?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Compliance ensures your rights are legally enforced online, helping remove unauthorized content and avoid liability.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-2" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-2">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      What types of DMCA actions does Trademark Pact handle?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      We prepare takedown requests, manage counter-notice responses, and advise on further legal steps as needed.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-3" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-3">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      How does Trademark Pact monitor for DMCA violations?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      We use advanced tools to track unauthorized use of your digital content across websites and platforms, notifying you promptly.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-4" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-4">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      How does Trademark Pact help avoid mistakes in DMCA enforcement?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Our expert legal review and careful management of all communications reduce errors that could weaken your enforcement efforts or expose you to risk.
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&q=80&auto=format"
                  alt="FAQ support"
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 'business-immigration-law' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-faqs">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-8">
                  Frequently Asked Questions
                </h2>
                <Accordion type="single" collapsible className="space-y-3">
                  <AccordionItem value="faq-0" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-0">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      What does Trademark Pact's business immigration law service cover?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Trademark Pact supports companies and individuals with visa applications, compliance guidance, and legal representation in immigration matters.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-1" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-1">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      Why is professional legal support important in business immigration?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Immigration laws are complex and frequently changing; expert guidance helps ensure applications are successful and compliance is maintained.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-2" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-2">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      What types of visas does Trademark Pact assist with?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      We assist with a variety of employment-based visas including H-1B, L-1, O-1, and other relevant categories.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-3" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-3">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      How does Trademark Pact help with immigration compliance?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      We advise on regulatory requirements, conduct compliance audits, and help implement policies to avoid violations.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-4" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-4">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      Can Trademark Pact represent my company in immigration disputes?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Yes, we provide legal advocacy in audits, investigations, and other immigration-related disputes.
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&q=80&auto=format"
                  alt="FAQ support"
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 'due-diligence-process' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-faqs">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-8">
                  Frequently Asked Questions
                </h2>
                <Accordion type="single" collapsible className="space-y-3">
                  <AccordionItem value="faq-0" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-0">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      What does Trademark Pact's due diligence process involve?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Trademark Pact conducts comprehensive reviews of legal, financial, and IP matters to inform your business decisions.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-1" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-1">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      Why is due diligence important in business transactions?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Due diligence helps identify risks, validate assets, and ensure compliance, reducing the chance of costly surprises.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-2" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-2">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      What areas does Trademark Pact focus on during due diligence?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      We examine contracts, intellectual property rights, regulatory compliance, and financial records.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-3" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-3">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      How are due diligence findings reported?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Our team provides clear, detailed reports with insights and recommendations tailored to your goals.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-4" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-4">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      How does Trademark Pact help avoid risks through due diligence?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      By thoroughly investigating all relevant areas, we help you make informed choices and mitigate potential liabilities.
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&q=80&auto=format"
                  alt="FAQ support"
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 'it-commercial-law' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-faqs">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-8">
                  Frequently Asked Questions
                </h2>
                <Accordion type="single" collapsible className="space-y-3">
                  <AccordionItem value="faq-0" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-0">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      What does Trademark Pact's IT commercial law service include?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Trademark Pact offers legal drafting, compliance guidance, and dispute support specific to technology and digital commerce businesses.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-1" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-1">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      Why is specialized IT commercial law important?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Technology businesses face unique legal challenges that require tailored contracts and compliance with evolving regulations.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-2" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-2">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      What types of contracts does Trademark Pact handle?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      We assist with software licenses, SaaS agreements, vendor contracts, and other IT-related commercial agreements.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-3" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-3">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      How does Trademark Pact support regulatory compliance?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      We advise on data privacy laws, cybersecurity regulations, and industry-specific legal requirements.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-4" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-4">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      Can Trademark Pact help resolve IT commercial disputes?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Yes, we provide legal guidance and representation in resolving contract disputes and liability claims.
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&q=80&auto=format"
                  alt="FAQ support"
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 'partnerships-llps' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-faqs">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-8">
                  Frequently Asked Questions
                </h2>
                <Accordion type="single" collapsible className="space-y-3">
                  <AccordionItem value="faq-0" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-0">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      What does Trademark Pact's partnership and LLP service cover?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Trademark Pact assists with drafting agreements, formation, compliance, and ongoing legal support for partnerships and LLPs.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-1" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-1">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      Why are formal agreements important in partnerships and LLPs?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Clear agreements prevent misunderstandings, define roles, and protect business interests in case of disputes.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-2" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-2">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      What legal requirements must partnerships and LLPs meet?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      They must comply with specific state and federal laws, including registration, tax obligations, and operational regulations.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-3" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-3">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      How does Trademark Pact support ongoing partnership management?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      We provide legal advice on amendments, conflict resolution, compliance, and changes in partnership structure.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-4" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-4">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      Can Trademark Pact help resolve disputes within partnerships or LLPs?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Yes, we offer expert legal guidance and representation to resolve conflicts efficiently.
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&q=80&auto=format"
                  alt="FAQ support"
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 'data-protection-privacy-law' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-faqs">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-8">
                  Frequently Asked Questions
                </h2>
                <Accordion type="single" collapsible className="space-y-3">
                  <AccordionItem value="faq-0" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-0">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      What does Trademark Pact's data protection and privacy law service include?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      We help businesses comply with privacy laws by drafting policies, conducting compliance checks, and advising on breach response.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-1" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-1">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      Why is compliance with data protection laws important?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Compliance protects your customers' personal information, avoids hefty fines, and builds consumer confidence.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-2" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-2">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      Which regulations does Trademark Pact assist with?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Our services cover GDPR, CCPA, and other national and international data privacy regulations.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-3" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-3">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      How does Trademark Pact help with data breach response?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      We develop legal response plans and assist with notifications and mitigation strategies following a breach.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-4" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-4">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      How can Trademark Pact reduce privacy-related risks?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      By ensuring your policies and practices meet legal standards, we help prevent violations and manage incidents effectively.
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&q=80&auto=format"
                  alt="FAQ support"
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 'limited-liability-company' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-faqs">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-8">
                  Frequently Asked Questions
                </h2>
                <Accordion type="single" collapsible className="space-y-3">
                  <AccordionItem value="faq-0" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-0">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      What does Trademark Pact's LLC service cover?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Trademark Pact assists with LLC formation, operating agreement drafting, compliance, and ongoing legal support.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-1" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-1">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      Why are operating agreements important for LLCs?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      They establish clear rules and responsibilities, reducing conflicts and protecting members' interests.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-2" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-2">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      What compliance requirements must LLCs meet?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      LLCs must adhere to state registration, annual filings, tax obligations, and other regulatory mandates.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-3" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-3">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      How does Trademark Pact support LLC management after formation?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      We provide advice on amendments, regulatory filings, and resolving member disputes.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-4" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-4">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      Can Trademark Pact help with LLC dispute resolution?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Yes, we offer expert legal representation and guidance to handle conflicts efficiently.
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&q=80&auto=format"
                  alt="FAQ support"
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 'c-corporation' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-faqs">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-8">
                  Frequently Asked Questions
                </h2>
                <Accordion type="single" collapsible className="space-y-3">
                  <AccordionItem value="faq-0" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-0">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      What does Trademark Pact's C Corporation service include?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      We assist with incorporation filings, bylaw drafting, compliance, and ongoing legal support for C Corporations.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-1" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-1">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      Why are corporate bylaws important for C Corporations?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Bylaws provide a framework for governance, helping resolve disputes and clarify roles within the company.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-2" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-2">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      What regulatory requirements apply to C Corporations?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      C Corporations must comply with federal and state filing, reporting, tax obligations, and shareholder meeting rules.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-3" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-3">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      How does Trademark Pact support ongoing C Corporation compliance?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Our team provides advice on regulatory filings, corporate governance, and shareholder relations.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-4" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-4">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      Can Trademark Pact assist with shareholder disputes?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Yes, we offer legal guidance and representation to resolve conflicts and protect your corporation's interests.
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&q=80&auto=format"
                  alt="FAQ support"
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 's-corporation' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-faqs">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-8">
                  Frequently Asked Questions
                </h2>
                <Accordion type="single" collapsible className="space-y-3">
                  <AccordionItem value="faq-0" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-0">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      What does Trademark Pact's S Corporation service cover?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      We assist with S Corporation election filings, bylaw preparation, compliance, and ongoing legal support.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-1" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-1">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      Why is filing IRS Form 2553 important for S Corporations?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      It officially elects your company's S Corporation status, which impacts taxation and legal structure.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-2" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-2">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      What legal documents are essential for S Corporations?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Corporate bylaws and shareholder agreements are critical to define governance and operational rules.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-3" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-3">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      How does Trademark Pact ensure compliance for S Corporations?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      We provide ongoing legal advice and assist with federal and state filing requirements to keep your business in good standing.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-4" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-4">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      Can Trademark Pact help with shareholder disputes in S Corporations?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Yes, we offer legal guidance and representation to resolve conflicts and protect your company's interests.
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&q=80&auto=format"
                  alt="FAQ support"
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 'non-profit' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-faqs">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-8">
                  Frequently Asked Questions
                </h2>
                <Accordion type="single" collapsible className="space-y-3">
                  <AccordionItem value="faq-0" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-0">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      What does Trademark Pact's non-profit service cover?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Trademark Pact assists with formation, governance document drafting, tax-exempt filings, and ongoing compliance support.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-1" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-1">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      Why is obtaining tax-exempt status important for non-profits?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Tax-exempt status enables your organization to receive tax-deductible donations and exempts you from certain taxes.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-2" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-2">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      What governance documents are essential for non-profits?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Bylaws, conflict of interest policies, and board governance documents are critical for operational integrity.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-3" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-3">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      How does Trademark Pact support ongoing non-profit compliance?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      We provide legal advice and assistance with state and federal filings, donor regulations, and governance matters.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-4" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-4">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      Can Trademark Pact help with non-profit disputes or challenges?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Yes, we offer legal representation and guidance to resolve issues while protecting your organization's mission.
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&q=80&auto=format"
                  alt="FAQ support"
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 'search-existing-patent' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-faqs">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-8">
                  Frequently Asked Questions
                </h2>
                <Accordion type="single" collapsible className="space-y-3">
                  <AccordionItem value="faq-0" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-0">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      What does Trademark Pact's existing patent search service involve?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      We conduct comprehensive searches and analyses of existing patents to help you evaluate your invention's uniqueness and risks.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-1" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-1">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      Why is conducting a patent search important?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      A thorough search helps prevent infringement, refines your invention, and improves your chances of successful patent approval.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-2" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-2">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      What patent databases does Trademark Pact search?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      We review U.S. and international patent databases, including USPTO, WIPO, and other relevant sources.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-3" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-3">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      How are patent search results delivered?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      We provide detailed reports summarizing relevant patents and offer strategic recommendations.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-4" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-4">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      How does Trademark Pact help avoid patent risks?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      By identifying similar existing patents early, we help you adjust your innovation and avoid costly legal disputes.
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&q=80&auto=format"
                  alt="FAQ support"
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 'patent-office-actions-support' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-faqs">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-8">
                  Frequently Asked Questions
                </h2>
                <Accordion type="single" collapsible className="space-y-3">
                  <AccordionItem value="faq-0" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-0">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      What does Trademark Pact's patent office actions support include?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      We review examiner office actions, prepare detailed responses, and manage timely filings to advance your patent applications.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-1" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-1">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      Why is responding properly to office actions important?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Accurate and strategic responses are critical to overcoming examiner objections and securing patent approval.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-2" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-2">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      What types of office actions can Trademark Pact assist with?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      We handle all office action types, including non-final, final, and restriction requirements.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-3" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-3">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      How does Trademark Pact ensure timely responses?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      We track deadlines meticulously and manage filings to prevent any lapses in your patent prosecution.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-4" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-4">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      Can Trademark Pact help if my application faces multiple office actions?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Yes, we provide ongoing support through successive office actions to guide your application toward approval.
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&q=80&auto=format"
                  alt="FAQ support"
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 'patent-application-submission' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-faqs">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-8">
                  Frequently Asked Questions
                </h2>
                <Accordion type="single" collapsible className="space-y-3">
                  <AccordionItem value="faq-0" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-0">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      What does Trademark Pact's patent application submission service entail?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      We prepare, review, and file patent applications, ensuring all legal and technical requirements are met.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-1" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-1">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      Why is professional submission support important?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Proper preparation and filing reduce errors, avoid delays, and improve your patent's chances of approval.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-2" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-2">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      Which patent offices does Trademark Pact file with?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      We file applications with the USPTO and other relevant national and international patent offices.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-3" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-3">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      How does Trademark Pact ensure application accuracy?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      We perform detailed reviews and quality checks before submission to comply with all patent office standards.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-4" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-4">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      Can Trademark Pact assist with follow-up after submission?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Yes, we monitor your application's progress and provide guidance on next steps throughout the process.
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&q=80&auto=format"
                  alt="FAQ support"
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 'legal-representation-patent' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-faqs">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-8">
                  Frequently Asked Questions
                </h2>
                <Accordion type="single" collapsible className="space-y-3">
                  <AccordionItem value="faq-0" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-0">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      What does Trademark Pact's legal representation for patent protection include?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      We provide legal advocacy, licensing negotiation, and defense services for patent owners facing infringement or disputes.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-1" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-1">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      Why is legal representation important for patent protection?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Professional legal support helps enforce your rights, resolve conflicts, and safeguard your inventions effectively.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-2" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-2">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      What types of patent disputes does Trademark Pact handle?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      We manage infringement cases, oppositions, appeals, and licensing disagreements.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-3" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-3">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      How does Trademark Pact assist with patent licensing?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      We draft and evaluate agreements to ensure your patents are protected while generating value.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-4" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-4">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      Can Trademark Pact represent me in patent litigation?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Yes, we offer skilled legal representation in court and before patent offices to defend your rights.
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&q=80&auto=format"
                  alt="FAQ support"
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 'amazon-brand-registry' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-faqs">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-8">
                  Frequently Asked Questions
                </h2>
                <Accordion type="single" collapsible className="space-y-3">
                  <AccordionItem value="faq-0" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-0">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      What is Trademark Pact's Amazon Brand Registry service?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      We assist brands in registering and protecting their trademarks with Amazon's Brand Registry program.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-1" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-1">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      Why is enrolling in Amazon Brand Registry important?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      It helps protect your brand from counterfeiters and unauthorized sellers on Amazon's platform.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-2" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-2">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      What documents are required for Amazon Brand Registry?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      A registered trademark and related ownership documentation are necessary for successful enrollment.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-3" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-3">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      How does Trademark Pact help with ongoing brand protection?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      We provide monitoring strategies and support to address infringement and misuse of your brand on Amazon.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-4" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-4">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      Can Trademark Pact assist with disputes on Amazon?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Yes, we help manage infringement claims and other brand-related issues within Amazon's system.
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&q=80&auto=format"
                  alt="FAQ support"
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 'bookkeeping-service' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-faqs">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-8">
                  Frequently Asked Questions
                </h2>
                <Accordion type="single" collapsible className="space-y-3">
                  <AccordionItem value="faq-0" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-0">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      What does Trademark Pact's bookkeeping service cover?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      We manage recording transactions, account reconciliations, and financial reporting for your business.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-1" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-1">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      Why is accurate bookkeeping important?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      It ensures compliance, supports informed decisions, and helps monitor your financial performance.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-2" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-2">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      How often are accounts reconciled?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Trademark Pact performs regular reconciliations to maintain accurate and consistent financial records.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-3" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-3">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      Can you provide customized financial reports?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Yes, we tailor reports to meet your business's specific needs and goals.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-4" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-4">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      How does Trademark Pact ensure bookkeeping accuracy?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Through professional oversight, systematic processes, and regular reviews to minimize errors.
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&q=80&auto=format"
                  alt="FAQ support"
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 'business-formation-services' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-faqs">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-8">
                  Frequently Asked Questions
                </h2>
                <Accordion type="single" collapsible className="space-y-3">
                  <AccordionItem value="faq-0" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-0">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      What do Trademark Pact's business formation services cover?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      We assist with entity selection, document drafting, filing, and compliance support for new businesses.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-1" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-1">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      Why is choosing the right business structure important?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      The structure affects your taxes, liability, and operational flexibility, impacting your long-term success.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-2" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-2">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      What documents are needed to form a business?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Articles of incorporation or organization, operating agreements, and registrations with state agencies.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-3" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-3">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      How does Trademark Pact help maintain compliance after formation?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      We provide guidance on ongoing filings, tax IDs, licenses, and regulatory requirements.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-4" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-4">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      Can Trademark Pact assist with amending business formation documents?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Yes, we help update your documents and filings as your business evolves.
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&q=80&auto=format"
                  alt="FAQ support"
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 'certificate-good-standing' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-faqs">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-8">
                  Frequently Asked Questions
                </h2>
                <Accordion type="single" collapsible className="space-y-3">
                  <AccordionItem value="faq-0" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-0">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      What is a certificate of good standing?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      It is an official document issued by state authorities that confirms your business complies with all regulatory requirements.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-1" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-1">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      When do I need a certificate of good standing?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      It's often required for business transactions, renewing licenses, securing financing, or proving your company's legal status.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-2" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-2">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      How does Trademark Pact obtain the certificate for my business?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      We verify compliance, prepare necessary paperwork, submit the application, and follow up to ensure timely issuance.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-3" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-3">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      How long does it take to receive a certificate of good standing?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Processing times vary by state, but Trademark Pact works to expedite requests wherever possible.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-4" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-4">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      Can Trademark Pact help me maintain my company's good standing?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Yes, we offer ongoing compliance support to help you avoid penalties and maintain your business's active status.
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&q=80&auto=format"
                  alt="FAQ support"
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 'company-dissolution' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-faqs">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-8">
                  Frequently Asked Questions
                </h2>
                <Accordion type="single" collapsible className="space-y-3">
                  <AccordionItem value="faq-0" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-0">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      What does Trademark Pact's company dissolution service cover?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      We handle the entire dissolution process, including document preparation, filings, and regulatory notifications.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-1" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-1">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      Why is legally dissolving a business important?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Proper dissolution protects you from future liabilities, tax obligations, and legal complications.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-2" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-2">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      What steps are involved in dissolving a company?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Steps include voting to dissolve, filing articles of dissolution, settling debts, and notifying authorities.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-3" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-3">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      How does Trademark Pact handle creditor notifications?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      We manage the notification process to ensure all creditors are properly informed and claims are addressed.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-4" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-4">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      Can Trademark Pact assist with final tax filings?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Yes, we provide guidance on final tax returns and ensure all tax obligations are settled.
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&q=80&auto=format"
                  alt="FAQ support"
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : slug === 'company-name-change' ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-faqs">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-8">
                  Frequently Asked Questions
                </h2>
                <Accordion type="single" collapsible className="space-y-3">
                  <AccordionItem value="faq-0" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-0">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      What does Trademark Pact's company name change service cover?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      We handle name availability checks, document preparation, filings, and updates to all official records.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-1" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-1">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      Why is checking name availability important?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      It ensures your new name is legally available and avoids rejection or conflicts with existing businesses.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-2" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-2">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      What documents are needed to change a company name?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Articles of amendment, updated operating agreements, and updated tax registration forms are typically required.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-3" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-3">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      How does Trademark Pact update my business name across records?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      We coordinate with state agencies, tax authorities, and licensing boards to ensure consistent updates.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="faq-4" className="bg-white rounded-xl border border-emerald-100 px-6" data-testid="service-faq-4">
                    <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                      Can Trademark Pact help with trademark updates after a name change?
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                      Yes, we can assist with updating your trademark registrations to reflect your new business name.
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&q=80&auto=format"
                  alt="FAQ support"
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : service.faqs && service.faqs.length > 0 ? (
        <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="service-faqs">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-8">
                  Frequently Asked Questions
                </h2>
                <Accordion type="single" collapsible className="space-y-3">
                  {service.faqs.map((faq, i) => (
                    <AccordionItem key={i} value={`faq-${i}`} className="bg-white rounded-xl border border-emerald-100 px-6" data-testid={`service-faq-${i}`}>
                      <AccordionTrigger className="font-heading font-semibold text-sm text-[#064E3B] hover:no-underline">
                        {faq.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                        {faq.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&q=80&auto=format"
                  alt="FAQ support"
                  className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {/* Testimonials */}
      <TestimonialSlider />

      {/* CTA */}
      {slug === 'active-monitoring-trademark-infringement' ? (
        <section className="py-12 lg:py-28 bg-[#064E3B]" data-testid="service-cta">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-6">
              Do Not Wait for Infringement to Find You.
            </h2>
            <p className="text-emerald-100 text-base lg:text-lg max-w-2xl mx-auto mb-8">
              Every day your trademark goes unmonitored increases the risk of unauthorized use quietly eroding your brand value and legal rights. Protect your mark by taking the first step with a free consultation.
            </p>
            <button
              onClick={() => openPopup()}
              className="bg-white text-[#064E3B] px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-emerald-50 transition-colors flex items-center gap-2 mx-auto btn-pulse"
              data-testid="service-cta-btn"
            >
              Start Monitoring My Trademark Now <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </section>
      ) : slug === 'clearance-search-services' ? (
        <section className="py-12 lg:py-28 bg-[#064E3B]" data-testid="service-cta">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-6">
              Don't Gamble with Your Brand's Future. Start with a Clearance Search.
            </h2>
            <p className="text-emerald-100 text-base lg:text-lg max-w-2xl mx-auto mb-8">
              Every day without a thorough trademark clearance search increases the risk of costly conflicts. Protect your brand by taking the first step with a free consultation.
            </p>
            <button
              onClick={() => openPopup()}
              className="bg-white text-[#064E3B] px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-emerald-50 transition-colors flex items-center gap-2 mx-auto btn-pulse"
              data-testid="service-cta-btn"
            >
              Start My Clearance Search <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </section>
      ) : slug === 'file-ttab-petition' ? (
        <section className="py-12 lg:py-28 bg-[#064E3B]" data-testid="service-cta">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-6">
              Do Not Let Conflicting Trademarks Threaten Your Brand Rights.
            </h2>
            <p className="text-emerald-100 text-base lg:text-lg max-w-2xl mx-auto mb-8">
              Every day without a properly filed TTAB petition leaves your trademark exposed to conflicting registrations and unfavorable decisions that could permanently harm your brand. Protect your rights by taking the first step with a free consultation.
            </p>
            <button
              onClick={() => openPopup()}
              className="bg-white text-[#064E3B] px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-emerald-50 transition-colors flex items-center gap-2 mx-auto btn-pulse"
              data-testid="service-cta-btn"
            >
              File My TTAB Petition Now <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </section>
      ) : slug === 'trademark-ownership-transfers' ? (
        <section className="py-12 lg:py-28 bg-[#064E3B]" data-testid="service-cta">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-6">
              Don't Leave Your Brand Assets Vulnerable During a Transfer.
            </h2>
            <p className="text-emerald-100 text-base lg:text-lg max-w-2xl mx-auto mb-8">
              Every unrecorded trademark assignment creates a window of legal risk for your business. Protect your brand ownership by taking the first step with a free consultation.
            </p>
            <button
              onClick={() => openPopup()}
              className="bg-white text-[#064E3B] px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-emerald-50 transition-colors flex items-center gap-2 mx-auto btn-pulse"
              data-testid="service-cta-btn"
            >
              Transfer My Trademark Now <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </section>
      ) : slug === 'submission-of-statements-of-use' ? (
        <section className="py-12 lg:py-28 bg-[#064E3B]" data-testid="service-cta">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-6">
              Do Not Let Your Trademark Application Stall at the Final Stage.
            </h2>
            <p className="text-emerald-100 text-base lg:text-lg max-w-2xl mx-auto mb-8">
              Every missed or incorrectly filed statement of use puts your entire trademark registration at risk. Protect the investment you have made in your brand by taking the first step with a free consultation.
            </p>
            <button
              onClick={() => openPopup()}
              className="bg-white text-[#064E3B] px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-emerald-50 transition-colors flex items-center gap-2 mx-auto btn-pulse"
              data-testid="service-cta-btn"
            >
              Submit My Statement of Use Now <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </section>
      ) : slug === 'support-response-trademark-office-actions' ? (
        <section className="py-12 lg:py-28 bg-[#064E3B]" data-testid="service-cta">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-6">
              Do Not Let a USPTO Refusal Stop Your Trademark Registration.
            </h2>
            <p className="text-emerald-100 text-base lg:text-lg max-w-2xl mx-auto mb-8">
              Every unanswered office action brings your application one step closer to permanent abandonment. Protect your brand investment by taking the first step with a free consultation.
            </p>
            <button
              onClick={() => openPopup()}
              className="bg-white text-[#064E3B] px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-emerald-50 transition-colors flex items-center gap-2 mx-auto btn-pulse"
              data-testid="service-cta-btn"
            >
              Respond to My Office Action Now <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </section>
      ) : slug === 'register-your-copyright' ? (
        <section className="py-12 lg:py-28 bg-[#064E3B]" data-testid="service-cta">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-6">
              Protect Your Creative Work with Trusted Copyright Registration
            </h2>
            <p className="text-emerald-100 text-base lg:text-lg max-w-2xl mx-auto mb-8">
              Every unregistered work leaves your creative rights exposed to infringement and legal uncertainty.
            </p>
            <button
              onClick={() => openPopup()}
              className="bg-white text-[#064E3B] px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-emerald-50 transition-colors flex items-center gap-2 mx-auto btn-pulse"
              data-testid="service-cta-btn"
            >
              Register My Copyright Now <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </section>
      ) : slug === 'copyright-enforcement-assistance' ? (
        <section className="py-12 lg:py-28 bg-[#064E3B]" data-testid="service-cta">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-6">
              Safeguard Your Copyrights Before Violations Escalate
            </h2>
            <p className="text-emerald-100 text-base lg:text-lg max-w-2xl mx-auto mb-8">
              Ignoring infringement can cause lasting harm to your work and brand. Protect your creative assets today with a free consultation from Trademark Pact.
            </p>
            <button
              onClick={() => openPopup()}
              className="bg-white text-[#064E3B] px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-emerald-50 transition-colors flex items-center gap-2 mx-auto btn-pulse"
              data-testid="service-cta-btn"
            >
              Protect My Copyright Now <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </section>
      ) : slug === 'copyright-transfers-licensing' ? (
        <section className="py-12 lg:py-28 bg-[#064E3B]" data-testid="service-cta">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-6">
              Secure Your Copyright Transactions with Confidence
            </h2>
            <p className="text-emerald-100 text-base lg:text-lg max-w-2xl mx-auto mb-8">
              Don't leave your creative rights vulnerable during transfers or licensing deals. Start with a free consultation from Trademark Pact to protect your intellectual property effectively.
            </p>
            <button
              onClick={() => openPopup()}
              className="bg-white text-[#064E3B] px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-emerald-50 transition-colors flex items-center gap-2 mx-auto btn-pulse"
              data-testid="service-cta-btn"
            >
              Manage My Copyright Transfer <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </section>
      ) : slug === 'legal-representation-copyright' ? (
        <section className="py-12 lg:py-28 bg-[#064E3B]" data-testid="service-cta">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-6">
              Protect Your Creative Works with Expert Legal Representation
            </h2>
            <p className="text-emerald-100 text-base lg:text-lg max-w-2xl mx-auto mb-8">
              Don't let copyright disputes threaten your creative assets. Secure your rights today with a free consultation from Trademark Pact.
            </p>
            <button
              onClick={() => openPopup()}
              className="bg-white text-[#064E3B] px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-emerald-50 transition-colors flex items-center gap-2 mx-auto btn-pulse"
              data-testid="service-cta-btn"
            >
              Protect My Copyright Now <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </section>
      ) : slug === 'copyright-portfolio-management' ? (
        <section className="py-12 lg:py-28 bg-[#064E3B]" data-testid="service-cta">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-6">
              Keep Your Copyright Assets Organized and Protected
            </h2>
            <p className="text-emerald-100 text-base lg:text-lg max-w-2xl mx-auto mb-8">
              Don't risk losing control over your creative works. Take charge of your copyright portfolio today with a free consultation from Trademark Pact.
            </p>
            <button
              onClick={() => openPopup()}
              className="bg-white text-[#064E3B] px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-emerald-50 transition-colors flex items-center gap-2 mx-auto btn-pulse"
              data-testid="service-cta-btn"
            >
              Manage My Copyright Portfolio <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </section>
      ) : slug === 'dmca-compliance' ? (
        <section className="py-12 lg:py-28 bg-[#064E3B]" data-testid="service-cta">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-6">
              Protect Your Digital Content with Trusted DMCA Compliance
            </h2>
            <p className="text-emerald-100 text-base lg:text-lg max-w-2xl mx-auto mb-8">
              Don't let online infringement damage your digital assets. Secure your rights today with a free consultation from Trademark Pact.
            </p>
            <button
              onClick={() => openPopup()}
              className="bg-white text-[#064E3B] px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-emerald-50 transition-colors flex items-center gap-2 mx-auto btn-pulse"
              data-testid="service-cta-btn"
            >
              Ensure DMCA Compliance <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </section>
      ) : slug === 'business-immigration-law' ? (
        <section className="py-12 lg:py-28 bg-[#064E3B]" data-testid="service-cta">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-6">
              Secure Your Workforce's Future with Expert Business Immigration Legal Support
            </h2>
            <p className="text-emerald-100 text-base lg:text-lg max-w-2xl mx-auto mb-8">
              Don't risk costly immigration errors. Start with a free consultation from Trademark Pact to protect your business and employees.
            </p>
            <button
              onClick={() => openPopup()}
              className="bg-white text-[#064E3B] px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-emerald-50 transition-colors flex items-center gap-2 mx-auto btn-pulse"
              data-testid="service-cta-btn"
            >
              Get Business Immigration Help <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </section>
      ) : slug === 'due-diligence-process' ? (
        <section className="py-12 lg:py-28 bg-[#064E3B]" data-testid="service-cta">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-6">
              Make Informed Business Decisions with Comprehensive Due Diligence
            </h2>
            <p className="text-emerald-100 text-base lg:text-lg max-w-2xl mx-auto mb-8">
              Protect your investments and reduce risks by partnering with Trademark Pact. Start your due diligence review today with a free consultation.
            </p>
            <button
              onClick={() => openPopup()}
              className="bg-white text-[#064E3B] px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-emerald-50 transition-colors flex items-center gap-2 mx-auto btn-pulse"
              data-testid="service-cta-btn"
            >
              Start Your Due Diligence Review Now <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </section>
      ) : slug === 'it-commercial-law' ? (
        <section className="py-12 lg:py-28 bg-[#064E3B]" data-testid="service-cta">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-6">
              Protect Your Technology Business with Expert IT Commercial Legal Support
            </h2>
            <p className="text-emerald-100 text-base lg:text-lg max-w-2xl mx-auto mb-8">
              Avoid costly legal issues by partnering with Trademark Pact. Schedule your free consultation today to secure your IT commercial interests.
            </p>
            <button
              onClick={() => openPopup()}
              className="bg-white text-[#064E3B] px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-emerald-50 transition-colors flex items-center gap-2 mx-auto btn-pulse"
              data-testid="service-cta-btn"
            >
              Get IT Commercial Law Assistance <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </section>
      ) : slug === 'partnerships-llps' ? (
        <section className="py-12 lg:py-28 bg-[#064E3B]" data-testid="service-cta">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-6">
              Build Strong, Legally Sound Partnerships and LLPs
            </h2>
            <p className="text-emerald-100 text-base lg:text-lg max-w-2xl mx-auto mb-8">
              Secure your business relationships with expert legal support from Trademark Pact. Start with a free consultation today.
            </p>
            <button
              onClick={() => openPopup()}
              className="bg-white text-[#064E3B] px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-emerald-50 transition-colors flex items-center gap-2 mx-auto btn-pulse"
              data-testid="service-cta-btn"
            >
              Establish My Partnership or LLP Now <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </section>
      ) : slug === 'data-protection-privacy-law' ? (
        <section className="py-12 lg:py-28 bg-[#064E3B]" data-testid="service-cta">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-6">
              Protect Your Business and Customers with Expert Data Privacy Support
            </h2>
            <p className="text-emerald-100 text-base lg:text-lg max-w-2xl mx-auto mb-8">
              Don't risk costly privacy violations. Secure your compliance today with a free consultation from Trademark Pact.
            </p>
            <button
              onClick={() => openPopup()}
              className="bg-white text-[#064E3B] px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-emerald-50 transition-colors flex items-center gap-2 mx-auto btn-pulse"
              data-testid="service-cta-btn"
            >
              Ensure Data Privacy Compliance Now <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </section>
      ) : slug === 'limited-liability-company' ? (
        <section className="py-12 lg:py-28 bg-[#064E3B]" data-testid="service-cta">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-6">
              Start Your LLC with Confidence and Legal Clarity
            </h2>
            <p className="text-emerald-100 text-base lg:text-lg max-w-2xl mx-auto mb-8">
              Protect your personal assets and build a solid business foundation with expert legal support from Trademark Pact. Schedule your free consultation today.
            </p>
            <button
              onClick={() => openPopup()}
              className="bg-white text-[#064E3B] px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-emerald-50 transition-colors flex items-center gap-2 mx-auto btn-pulse"
              data-testid="service-cta-btn"
            >
              Form My LLC Now <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </section>
      ) : slug === 'c-corporation' ? (
        <section className="py-12 lg:py-28 bg-[#064E3B]" data-testid="service-cta">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-6">
              Incorporate Your Business with Confidence and Legal Precision
            </h2>
            <p className="text-emerald-100 text-base lg:text-lg max-w-2xl mx-auto mb-8">
              Start your C Corporation journey with expert legal support. Schedule a free consultation with Trademark Pact today.
            </p>
            <button
              onClick={() => openPopup()}
              className="bg-white text-[#064E3B] px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-emerald-50 transition-colors flex items-center gap-2 mx-auto btn-pulse"
              data-testid="service-cta-btn"
            >
              Incorporate My C Corporation Now <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </section>
      ) : slug === 's-corporation' ? (
        <section className="py-12 lg:py-28 bg-[#064E3B]" data-testid="service-cta">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-6">
              Establish Your S Corporation with Confidence and Expert Legal Support
            </h2>
            <p className="text-emerald-100 text-base lg:text-lg max-w-2xl mx-auto mb-8">
              Get started on your S Corporation formation today with a free consultation from Trademark Pact.
            </p>
            <button
              onClick={() => openPopup()}
              className="bg-white text-[#064E3B] px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-emerald-50 transition-colors flex items-center gap-2 mx-auto btn-pulse"
              data-testid="service-cta-btn"
            >
              Form My S Corporation Now <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </section>
      ) : slug === 'non-profit' ? (
        <section className="py-12 lg:py-28 bg-[#064E3B]" data-testid="service-cta">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-6">
              Launch and Grow Your Non-Profit with Confidence
            </h2>
            <p className="text-emerald-100 text-base lg:text-lg max-w-2xl mx-auto mb-8">
              Start your non-profit journey with expert legal support. Schedule a free consultation with Trademark Pact today.
            </p>
            <button
              onClick={() => openPopup()}
              className="bg-white text-[#064E3B] px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-emerald-50 transition-colors flex items-center gap-2 mx-auto btn-pulse"
              data-testid="service-cta-btn"
            >
              Establish My Non-Profit Now <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </section>
      ) : slug === 'search-existing-patent' ? (
        <section className="py-12 lg:py-28 bg-[#064E3B]" data-testid="service-cta">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-6">
              Protect Your Innovation with a Professional Patent Search
            </h2>
            <p className="text-emerald-100 text-base lg:text-lg max-w-2xl mx-auto mb-8">
              Don't risk patent issues down the line. Begin your patent search today with a free consultation from Trademark Pact.
            </p>
            <button
              onClick={() => openPopup()}
              className="bg-white text-[#064E3B] px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-emerald-50 transition-colors flex items-center gap-2 mx-auto btn-pulse"
              data-testid="service-cta-btn"
            >
              Start My Patent Search Now <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </section>
      ) : slug === 'patent-office-actions-support' ? (
        <section className="py-12 lg:py-28 bg-[#064E3B]" data-testid="service-cta">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-6">
              Navigate Patent Office Actions with Confidence and Expert Support
            </h2>
            <p className="text-emerald-100 text-base lg:text-lg max-w-2xl mx-auto mb-8">
              Keep your patent application moving forward. Start with a free consultation from Trademark Pact today.
            </p>
            <button
              onClick={() => openPopup()}
              className="bg-white text-[#064E3B] px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-emerald-50 transition-colors flex items-center gap-2 mx-auto btn-pulse"
              data-testid="service-cta-btn"
            >
              Get Patent Office Actions Support Now <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </section>
      ) : slug === 'patent-application-submission' ? (
        <section className="py-12 lg:py-28 bg-[#064E3B]" data-testid="service-cta">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-6">
              Secure Your Invention with Expert Patent Application Submission
            </h2>
            <p className="text-emerald-100 text-base lg:text-lg max-w-2xl mx-auto mb-8">
              Protect your innovation by filing correctly the first time. Start with a free consultation from Trademark Pact today.
            </p>
            <button
              onClick={() => openPopup()}
              className="bg-white text-[#064E3B] px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-emerald-50 transition-colors flex items-center gap-2 mx-auto btn-pulse"
              data-testid="service-cta-btn"
            >
              Submit My Patent Application Now <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </section>
      ) : slug === 'legal-representation-patent' ? (
        <section className="py-12 lg:py-28 bg-[#064E3B]" data-testid="service-cta">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-6">
              Defend Your Patents with Confidence and Expert Legal Support
            </h2>
            <p className="text-emerald-100 text-base lg:text-lg max-w-2xl mx-auto mb-8">
              Secure your innovations through effective legal representation. Schedule a free consultation with Trademark Pact today.
            </p>
            <button
              onClick={() => openPopup()}
              className="bg-white text-[#064E3B] px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-emerald-50 transition-colors flex items-center gap-2 mx-auto btn-pulse"
              data-testid="service-cta-btn"
            >
              Protect My Patent Now <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </section>
      ) : slug === 'amazon-brand-registry' ? (
        <section className="py-12 lg:py-28 bg-[#064E3B]" data-testid="service-cta">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-6">
              Secure Your Brand's Future on Amazon with Expert Registry Support
            </h2>
            <p className="text-emerald-100 text-base lg:text-lg max-w-2xl mx-auto mb-8">
              Protect your brand and grow your business confidently. Start with a free consultation from Trademark Pact today.
            </p>
            <button
              onClick={() => openPopup()}
              className="bg-white text-[#064E3B] px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-emerald-50 transition-colors flex items-center gap-2 mx-auto btn-pulse"
              data-testid="service-cta-btn"
            >
              Register My Brand on Amazon Now <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </section>
      ) : slug === 'bookkeeping-service' ? (
        <section className="py-12 lg:py-28 bg-[#064E3B]" data-testid="service-cta">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-6">
              Keep Your Finances Organized and Accurate with Expert Bookkeeping
            </h2>
            <p className="text-emerald-100 text-base lg:text-lg max-w-2xl mx-auto mb-8">
              Take control of your business finances today with a free consultation from Trademark Pact.
            </p>
            <button
              onClick={() => openPopup()}
              className="bg-white text-[#064E3B] px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-emerald-50 transition-colors flex items-center gap-2 mx-auto btn-pulse"
              data-testid="service-cta-btn"
            >
              Get Professional Bookkeeping Now <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </section>
      ) : slug === 'business-formation-services' ? (
        <section className="py-12 lg:py-28 bg-[#064E3B]" data-testid="service-cta">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-6">
              Launch Your Business with Confidence and Expert Legal Support
            </h2>
            <p className="text-emerald-100 text-base lg:text-lg max-w-2xl mx-auto mb-8">
              Get started on your business journey today with a free consultation from Trademark Pact.
            </p>
            <button
              onClick={() => openPopup()}
              className="bg-white text-[#064E3B] px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-emerald-50 transition-colors flex items-center gap-2 mx-auto btn-pulse"
              data-testid="service-cta-btn"
            >
              Start My Business Formation Now <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </section>
      ) : slug === 'certificate-good-standing' ? (
        <section className="py-12 lg:py-28 bg-[#064E3B]" data-testid="service-cta">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-6">
              Maintain Your Business's Good Standing with Expert Assistance
            </h2>
            <p className="text-emerald-100 text-base lg:text-lg max-w-2xl mx-auto mb-8">
              Protect your company's reputation and legal status with trusted support from Trademark Pact. Start with a free consultation today.
            </p>
            <button
              onClick={() => openPopup()}
              className="bg-white text-[#064E3B] px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-emerald-50 transition-colors flex items-center gap-2 mx-auto btn-pulse"
              data-testid="service-cta-btn"
            >
              Get My Certificate of Good Standing Now <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </section>
      ) : slug === 'company-dissolution' ? (
        <section className="py-12 lg:py-28 bg-[#064E3B]" data-testid="service-cta">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-6">
              Close Your Business Properly and Avoid Future Liabilities
            </h2>
            <p className="text-emerald-100 text-base lg:text-lg max-w-2xl mx-auto mb-8">
              Ensure a smooth and legally compliant dissolution with expert guidance from Trademark Pact. Start with a free consultation today.
            </p>
            <button
              onClick={() => openPopup()}
              className="bg-white text-[#064E3B] px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-emerald-50 transition-colors flex items-center gap-2 mx-auto btn-pulse"
              data-testid="service-cta-btn"
            >
              Dissolve My Company Now <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </section>
      ) : slug === 'company-name-change' ? (
        <section className="py-12 lg:py-28 bg-[#064E3B]" data-testid="service-cta">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-6">
              Change Your Company Name with Professional Legal Support
            </h2>
            <p className="text-emerald-100 text-base lg:text-lg max-w-2xl mx-auto mb-8">
              Ensure a smooth transition to your new business identity with expert assistance from Trademark Pact. Start with a free consultation today.
            </p>
            <button
              onClick={() => openPopup()}
              className="bg-white text-[#064E3B] px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-emerald-50 transition-colors flex items-center gap-2 mx-auto btn-pulse"
              data-testid="service-cta-btn"
            >
              Change My Company Name Now <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </section>
      ) : (
        <CTABanner />
      )}
    </div>
  );
}
